import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { AppSidebar } from '@/components/Sidebar';
import { Gallery } from '@/components/Gallery';
import { DetailPanel } from '@/components/DetailPanel';
import { Drawer, DrawerContent } from '@/components/ui/drawer';

import { BulkTagDialog } from '@/components/BulkTagDialog';
import { EmptyTrashDialog } from '@/components/EmptyTrashDialog';
import { DeletePhotosDialog } from '@/components/DeletePhotosDialog';
import { ProcessingPanel, ProcessingFile } from '@/components/ProcessingPanel';
import { ReprocessProgressDialog } from '@/components/ReprocessProgressDialog';
import { ReprocessNoTagsDialog } from '@/components/ReprocessNoTagsDialog';
import { DuplicateFilesDialog } from '@/components/DuplicateFilesDialog';
import { CreateFolderDialog } from '@/components/CreateFolderDialog';
import { Toolbar } from '@/components/Toolbar';
import { MobileToolbar } from '@/components/MobileToolbar';
import { MobileBottomBar } from '@/components/MobileBottomBar';
import { Photo, Folder } from '@/types';
import { getDefaultFolders, simulateUpload } from '@/utils/mockData';
import { toast } from 'sonner';
import { generateImageEmbedding } from '@/utils/clipEmbeddings';
import { useToast } from '@/hooks/use-toast';
import { Menu, User as UserIcon } from 'lucide-react';
import { debug } from '@/utils/debug';
import { addSignedUrlsToPhotos } from '@/utils/signedUrls';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useIsMobile } from '@/hooks/use-mobile';

const Index = () => {
  const { toast: toastHook } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState<User | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [folders, setFolders] = useState<Folder[]>(getDefaultFolders());
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<string[]>([]);
  const [processingFiles, setProcessingFiles] = useState<ProcessingFile[]>([]);
  const [showProcessingPanel, setShowProcessingPanel] = useState(false);
  const [isUploadPaused, setIsUploadPaused] = useState(false);
  const isUploadPausedRef = useRef(false);
  const [uploadQueue, setUploadQueue] = useState<File[]>([]);
  const uploadAbortController = useRef<AbortController | null>(null);
  // Keep the last started batch to rebuild queue on resume if needed
  const lastBatchRef = useRef<File[] | null>(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateFiles, setDuplicateFiles] = useState<File[]>([]);
  const [duplicateFilenames, setDuplicateFilenames] = useState<string[]>([]);
  const [skipAllDuplicates, setSkipAllDuplicates] = useState(false);
  // Buffer for duplicate decision flow
  const pendingNonDuplicatesRef = useRef<File[] | null>(null);
  const pendingDuplicatesRef = useRef<File[] | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBulkTagDialog, setShowBulkTagDialog] = useState(false);
  const [showEmptyTrashDialog, setShowEmptyTrashDialog] = useState(false);
  const [showDeletePhotosDialog, setShowDeletePhotosDialog] = useState(false);
  const [showNoTagsDialog, setShowNoTagsDialog] = useState(false);
  const [showCreateFolderDialog, setShowCreateFolderDialog] = useState(false);
  const [isEmptyingTrash, setIsEmptyingTrash] = useState(false);
  const [isDeletingPhotos, setIsDeletingPhotos] = useState(false);
  const [gridSize, setGridSize] = useState(4);
  const [thumbnailSize, setThumbnailSize] = useState<'small' | 'medium' | 'large'>(() => {
    const sizeParam = new URLSearchParams(window.location.search).get('size');
    return (sizeParam as 'small' | 'medium' | 'large') || 'medium';
  });
  const [viewMode, setViewMode] = useState<'desktop' | 'icon'>('desktop');
  const [availableTags, setAvailableTags] = useState<Array<{ id: string; name: string; color?: string }>>([]);
  const [availableFolders, setAvailableFolders] = useState<Array<{ id: string; name: string; color?: string }>>([]);
  const [isReprocessing, setIsReprocessing] = useState(false);
  const shouldStopReprocessingRef = useRef(false);
  const [totalStorageBytes, setTotalStorageBytes] = useState<number>(0);
  const [photoLimitForOrg, setPhotoLimitForOrg] = useState<number>(10000);
  const [perUploadLimit, setPerUploadLimit] = useState<number>(250);
  const isMobile = useIsMobile();
  const [reprocessProgress, setReprocessProgress] = useState({
    total: 0,
    processed: 0,
    successful: 0,
    failed: 0,
    currentPhoto: '',
  });
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Realtime insert suppression while alerts/dialogs are open
  const suppressRealtimeInsertsRef = useRef(false);
  const pendingRealtimeInsertsRef = useRef<any[]>([]);
  
  // Read filters from URL params
  const searchQuery = searchParams.get('search') || '';
  const currentPage = parseInt(searchParams.get('page') || '1');
  const urlThumbnailSize = (searchParams.get('size') as 'small' | 'medium' | 'large') || 'medium';
  
  // Local search state for immediate UI feedback
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const searchDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Prefer consolidated JSON filters param, with legacy fallback
  let parsedFilters: any = {};
      const filtersParam = searchParams.get('filters');
      if (filtersParam) {
        try {
          const rawFilters = JSON.parse(filtersParam);
          // Validate filter structure
          if (typeof rawFilters === 'object' && rawFilters !== null) {
            parsedFilters = {
              ...rawFilters,
              trash: typeof rawFilters.trash === 'boolean' ? rawFilters.trash : undefined,
              tags: Array.isArray(rawFilters.tags) ? rawFilters.tags.filter((t: any) => typeof t === 'string') : undefined,
              folders: Array.isArray(rawFilters.folders) ? rawFilters.folders.filter((f: any) => typeof f === 'string') : undefined,
              colors: Array.isArray(rawFilters.colors) ? rawFilters.colors.filter((c: any) => typeof c === 'string') : undefined,
              ratings: Array.isArray(rawFilters.ratings) ? rawFilters.ratings.filter((r: any) => typeof r === 'number' && r >= 0 && r <= 5) : undefined,
              is_favorite: typeof rawFilters.is_favorite === 'boolean' ? rawFilters.is_favorite : undefined,
            };
          }
    } catch {
      parsedFilters = {};
    }
  }

  // Legacy params (for backward compatibility)
  let legacyTags: string[] = [];
  try {
    const tagsParam = searchParams.get('tags');
    if (tagsParam) {
      const parsed = JSON.parse(tagsParam);
      legacyTags = Array.isArray(parsed) ? parsed.filter((t: any) => typeof t === 'string') : [];
    }
  } catch (error) {
    console.error('Failed to parse legacy tags:', error);
  }
  
  let legacyFolders: string[] = [];
  try {
    const foldersParam = searchParams.get('folders');
    if (foldersParam) {
      const parsed = JSON.parse(foldersParam);
      legacyFolders = Array.isArray(parsed) ? parsed.filter((f: any) => typeof f === 'string') : [];
    }
  } catch (error) {
    console.error('Failed to parse legacy folders:', error);
  }
  
  let legacyRating: number | null = null;
  try {
    const ratingParam = searchParams.get('rating');
    if (ratingParam) {
      const parsed = JSON.parse(ratingParam);
      if (typeof parsed === 'number' && parsed >= 0 && parsed <= 5) {
        legacyRating = parsed;
      }
    }
  } catch (error) {
    console.error('Failed to parse legacy rating:', error);
  }
  
  const legacyType = (searchParams.get('type') as 'all' | 'has_pii' | 'has_tag' | 'no_tags') || undefined;
  const legacyTrash = searchParams.get('trash') === 'true';

  // Map boolean flags to type when provided (supports filters={"has_tags":true} style)
  let typeFromBooleans: 'all' | 'has_pii' | 'has_tag' | 'no_tags' | 'no_folders' | 'has_code' | 'has_person' | 'is_illustrated' | 'has_error' | undefined;
  if (parsedFilters.has_tags) typeFromBooleans = 'has_tag';
  else if (parsedFilters.no_tags) typeFromBooleans = 'no_tags';
  else if (parsedFilters.no_folders) typeFromBooleans = 'no_folders';
  else if (parsedFilters.has_pii) typeFromBooleans = 'has_pii';
  else if (parsedFilters.has_code) typeFromBooleans = 'has_code';
  else if (parsedFilters.has_person) typeFromBooleans = 'has_person';
  else if (parsedFilters.is_illustrated) typeFromBooleans = 'is_illustrated';
  else if (parsedFilters.has_error) typeFromBooleans = 'has_error';

  const selectedTags = parsedFilters.tags ?? legacyTags;
  const selectedFolderFilters = parsedFilters.folders ?? legacyFolders;
  const selectedColors = parsedFilters.colors ?? [];
  const includeSimilarColors = parsedFilters.includeSimilarColors ?? false;
  const ratingFilter = parsedFilters.rating ?? legacyRating;
  const photoTypeFilter = (typeFromBooleans ?? (parsedFilters.type as 'all' | 'has_pii' | 'has_tag' | 'no_tags' | 'no_folders' | 'has_code' | 'has_person' | 'is_illustrated' | 'has_error') ?? legacyType) || 'all';
  const showTrash = (typeof parsedFilters.trash === 'boolean' ? parsedFilters.trash : legacyTrash);
  const favoriteFilter = parsedFilters.favorite ?? false;
  const activeQuickLinkParam = searchParams.get('view') as 'all' | 'trash' | 'pii' | 'notags' | 'nofolders' | 'hascode' | 'hasperson' | 'illustrated' | 'error' | null;
  const activeQuickLink: 'all' | 'trash' | 'pii' | 'notags' | 'nofolders' | 'hascode' | 'hasperson' | 'illustrated' | 'error' =
    activeQuickLinkParam ??
    (showTrash ? 'trash'
      : photoTypeFilter === 'no_tags' ? 'notags'
      : photoTypeFilter === 'no_folders' ? 'nofolders'
      : photoTypeFilter === 'has_pii' ? 'pii'
      : photoTypeFilter === 'has_code' ? 'hascode'
      : photoTypeFilter === 'has_person' ? 'hasperson'
      : photoTypeFilter === 'is_illustrated' ? 'illustrated'
      : photoTypeFilter === 'has_error' ? 'error'
      : 'all');
  const sortField = (parsedFilters.sortField as 'created_at' | 'updated_at' | 'filename' | 'rating') || 'created_at';
  const sortDirection = (parsedFilters.sortDirection as 'asc' | 'desc') || 'desc';
  
  // Sync local search with URL search
  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  // Debounced search handler
  const handleSearchChange = useCallback((value: string) => {
    // Update UI immediately
    setLocalSearchQuery(value);
    
    // Clear existing timer
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }
    
    // Debounce the actual search update
    searchDebounceTimerRef.current = setTimeout(() => {
      updateFilters({ search: value || null, page: 1 });
    }, 300);
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, []);

  // Helper to update URL params while consolidating into filters JSON
  const updateFilters = (updates: Record<string, any>) => {
    const newParams = new URLSearchParams(searchParams);
    const before = newParams.toString();

    // Start from current filters JSON
    let currentFilters: any = {};
    const existing = newParams.get('filters');
    if (existing) {
      try { currentFilters = JSON.parse(existing); } catch { currentFilters = {}; }
    }

    Object.entries(updates).forEach(([key, value]) => {
      if (key === 'search' || key === 'page') {
        if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
          newParams.delete(key);
        } else {
          newParams.set(key, String(value));
        }
        return;
      }
      // Ignore UI-only view key in URL and filters
      if (key === 'view') {
        newParams.delete('view');
        return;
      }

      // Everything else goes inside the consolidated filters JSON
      // Special handling: normalize type -> boolean flags
      if (key === 'type') {
        // Reset boolean flags
        delete currentFilters.has_tags;
        delete currentFilters.no_tags;
        delete currentFilters.no_folders;
        delete currentFilters.has_pii;
        delete currentFilters.has_code;
        delete currentFilters.has_person;
        delete currentFilters.is_illustrated;
        delete currentFilters.has_error;
        
        if (value === 'has_tag') currentFilters.has_tags = true;
        else if (value === 'no_tags') currentFilters.no_tags = true;
        else if (value === 'no_folders') currentFilters.no_folders = true;
        else if (value === 'has_pii') currentFilters.has_pii = true;
        else if (value === 'has_code') currentFilters.has_code = true;
        else if (value === 'has_person') currentFilters.has_person = true;
        else if (value === 'is_illustrated') currentFilters.is_illustrated = true;
        else if (value === 'has_error') currentFilters.has_error = true;
        else {
          // 'all' -> no flags
        }
        // Do not persist raw `type` in JSON
        delete currentFilters.type;
        return;
      }

      // Direct boolean flags support (has_tags, no_tags, has_pii, has_code, has_person, is_illustrated, has_error)
      if (['has_tags', 'no_tags', 'no_folders', 'has_pii', 'has_code', 'has_person', 'is_illustrated', 'has_error', 'trash', 'favorite', 'tags', 'folders', 'colors', 'includeSimilarColors', 'rating'].includes(key)) {
        if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0) || value === false) {
          delete currentFilters[key];
        } else {
          currentFilters[key] = value;
        }
        return;
      }

      // Fallback: store unknown keys inside filters JSON
      if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
        delete currentFilters[key];
      } else {
        currentFilters[key] = value;
      }
    });

    // Clean legacy individual params
    ['tags','folders','rating','type','trash','view'].forEach(k => newParams.delete(k));

    // Write filters JSON or remove if empty
    const isEmpty = Object.keys(currentFilters).length === 0;
    if (isEmpty) newParams.delete('filters');
    else newParams.set('filters', JSON.stringify(currentFilters));

    const after = newParams.toString();
    if (after === before) return; // Avoid unnecessary updates that can cause loops
    setSearchParams(newParams);
  };

  // Check authentication
  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (!session) {
        navigate('/auth');
      }
    });

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (!session) {
        navigate('/auth');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  // Set "All Photos" as default view on initial load if no view/folder/filter is selected
  useEffect(() => {
    if (!user) return;
    
    const hasAnyFilter = 
      activeQuickLink !== 'all' || 
      selectedFolderId !== null || 
      selectedTags.length > 0 || 
      selectedFolderFilters.length > 0 || 
      selectedColors.length > 0 || 
      ratingFilter !== null || 
      showTrash || 
      favoriteFilter ||
      searchQuery !== '' ||
      photoTypeFilter !== 'all';
    
    // If no filters are applied and no view is set, default to "All Photos"
    if (!hasAnyFilter && !searchParams.has('view')) {
      updateFilters({ view: 'all' });
    }
  }, [user]); // Only run once when user is loaded

  // Load photos with backend filtering, search, and pagination
  const loadPhotos = async (options?: {
    page?: number;
    itemsPerPage?: number;
    search?: string;
    tagFilter?: string[];
    folderFilter?: string[];
    colorFilter?: string[];
    includeSimilarColors?: boolean;
    ratingFilter?: { operator: 'gte' | 'lte' | 'eq'; value: number } | null;
    photoTypeFilter?: 'all' | 'has_pii' | 'has_tag' | 'no_tags' | 'no_folders' | 'has_code' | 'has_person' | 'is_illustrated' | 'has_error' | null;
    showTrash?: boolean;
    favoriteFilter?: boolean;
    folderId?: string | null;
    sortField?: 'created_at' | 'updated_at' | 'filename' | 'rating';
    sortDirection?: 'asc' | 'desc';
  }) => {
    if (!user) return { photos: [], totalCount: 0 };

    setIsLoadingPhotos(true);
    try {
      const { data: userOrgs } = await supabase
        .from('user_organizations')
        .select('organization_id')
        .eq('user_id', user.id)
        .limit(1);

      if (!userOrgs || userOrgs.length === 0) return { photos: [], totalCount: 0 };

      const orgId = userOrgs[0].organization_id;
      const page = options?.page || 1;
      const itemsPerPage = options?.itemsPerPage || 60;

      // Prepare filters object
      const filters: any = {
        trash: options?.showTrash || false,
      };

      // Favorite filter
      if (options?.favoriteFilter) {
        filters.is_favorite = true;
      }

      // Photo type filter
      if (options?.photoTypeFilter === 'no_tags') {
        filters.no_tags = true;
      } else if (options?.photoTypeFilter === 'no_folders') {
        filters.no_folders = true;
      } else if (options?.photoTypeFilter === 'has_code') {
        filters.has_code = true;
      } else if (options?.photoTypeFilter === 'has_person') {
        filters.has_person = true;
      } else if (options?.photoTypeFilter === 'has_error') {
        filters.has_error = true;
      } else if (options?.photoTypeFilter === 'is_illustrated') {
        filters.is_illustrated = true;
      } else if (options?.photoTypeFilter === 'has_tag') {
        // This will be handled by tag filter
        if (!options?.tagFilter || options.tagFilter.length === 0) {
          // No specific tags selected, just want photos with ANY tags
          const { data: allTaggedPhotos } = await supabase
            .from('photo_tags')
            .select('photo_id');
          
          const uniquePhotoIds = Array.from(new Set(allTaggedPhotos?.map(pt => pt.photo_id) || []));
          if (uniquePhotoIds.length === 0) {
            setPhotos([]);
            return { photos: [], totalCount: 0 };
          }
        }
      } else if (options?.photoTypeFilter === 'has_pii') {
        filters.photoTypes = ['has_pii'];
      }

      // Tag filter
      if (options?.tagFilter && options.tagFilter.length > 0) {
        filters.tags = options.tagFilter;
      }

      // Color filter
      if (options?.colorFilter && options.colorFilter.length > 0) {
        filters.colors = options.colorFilter;
        filters.includeSimilarColors = options.includeSimilarColors || false;
      }

      // Folder filters
      const folderFilters = [];
      if (options?.folderId) {
        folderFilters.push(options.folderId);
      }
      if (options?.folderFilter && options.folderFilter.length > 0) {
        folderFilters.push(...options.folderFilter);
      }
      if (folderFilters.length > 0) {
        filters.folders = folderFilters;
      }

      // Rating filter
      if (options?.ratingFilter) {
        const { operator, value } = options.ratingFilter;
        // Convert to array of ratings based on operator
        if (operator === 'eq') {
          filters.ratings = [value];
        } else if (operator === 'gte') {
          filters.ratings = [value, value + 1, value + 2, value + 3, value + 4, 5].filter(r => r <= 5);
        } else if (operator === 'lte') {
          filters.ratings = [0, 1, 2, 3, 4, value].filter(r => r <= value);
        }
      }

      // Prepare search embedding if search query exists
      let searchEmbedding: number[] | undefined;
      if (options?.search && options.search.trim()) {
        try {
          // Only generate embedding if search looks like it could be an image URL
          if (options.search.startsWith('http') || options.search.startsWith('data:')) {
            searchEmbedding = await generateImageEmbedding(options.search.trim());
          }
        } catch (error) {
          console.error('Error generating search embedding:', error);
        }
      }

      // Call the unified query-photos edge function
      const { data: result, error } = await supabase.functions.invoke('query-photos', {
        body: {
          organizationId: orgId,
          filters,
          search: options?.search?.trim() || undefined,
          searchEmbedding,
          sort: {
            field: options?.sortField || 'created_at',
            direction: options?.sortDirection || 'desc'
          },
          pagination: {
            offset: (page - 1) * itemsPerPage,
            limit: itemsPerPage
          }
        }
      });

      if (error) {
        console.warn('Error loading photos (soft):', error);
        return { photos: [], totalCount: 0 };
      }

      const photosData = result.results || [];
      const totalCount = result.count || 0;

      // Load photo tags and colors for the current page photos
      if (photosData.length > 0) {
        const photoIds = photosData.map((p: any) => p.id);
        
        const { data: photoTagsData } = await supabase
          .from('photo_tags')
          .select('photo_id, tag_id')
          .in('photo_id', photoIds);

        const photoTagsMap = new Map<string, string[]>();
        photoTagsData?.forEach((pt: any) => {
          const existing = photoTagsMap.get(pt.photo_id) || [];
          photoTagsMap.set(pt.photo_id, [...existing, pt.tag_id]);
        });

        // Load photo colors
        const { data: photoColorsData } = await supabase
          .from('photo_colors')
          .select(`
            photo_id,
            position,
            color_id,
            colors (id, hex, r, g, b, ok_l, ok_a, ok_b)
          `)
          .in('photo_id', photoIds)
          .order('position');

        const photoColorsMap = new Map<string, any[]>();
        photoColorsData?.forEach((pc: any) => {
          const existing = photoColorsMap.get(pc.photo_id) || [];
          photoColorsMap.set(pc.photo_id, [...existing, pc.colors]);
        });

        // Generate signed URLs for photos
        const photosWithSignedUrls = await addSignedUrlsToPhotos(photosData);
        
        const mappedPhotos = photosWithSignedUrls.map((p: any) => ({
          id: p.id,
          filename: p.filename,
          url: p.url,
          tags: [],
          tagIds: photoTagsMap.get(p.id) || [],
          description: p.description || '',
          uploadDate: p.created_at,
          updatedAt: p.updated_at,
          fileSize: p.file_size,
          fileType: p.file_type,
          dimensions: { width: p.width || 0, height: p.height || 0 },
          colors: photoColorsMap.get(p.id) || [],
          rating: p.rating || 0,
          is_favorite: p.is_favorite ?? false,
          processingStatus: (p.processing_status || 'pending') as 'pending' | 'processing' | 'complete' | 'failed',
          has_pii: p.has_pii ?? false,
          pii_details: p.pii_details ?? null,
          deleted_at: p.deleted_at ?? null,
        }));

        setPhotos(mappedPhotos);
        return { photos: mappedPhotos, totalCount };
      }

      setPhotos([]);
      return { photos: [], totalCount };
    } catch (error) {
      console.warn('Error in loadPhotos (soft):', error);
      return { photos: [], totalCount: 0 };
    } finally {
      setIsLoadingPhotos(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadPhotos();
  }, [user]);

  // Get organization ID for BulkTagDialog
  const [organizationId, setOrganizationId] = useState<string>('');
  const [hasLoadedOrganization, setHasLoadedOrganization] = useState(false);
  
  useEffect(() => {
    if (!user || hasLoadedOrganization) return;
    
    supabase
      .from('user_organizations')
      .select('organization_id')
      .eq('user_id', user.id)
      .limit(1)
      .then(({ data }) => {
        if (data && data.length > 0) {
          setOrganizationId(data[0].organization_id);
          setHasLoadedOrganization(true);
        }
      });
  }, [user, hasLoadedOrganization]);

  // Load tags and folders for filters
  const loadFoldersFromDB = async () => {
    if (!organizationId) return;
    
    // Load folders with cover photos and photo counts
    const { data: foldersData, error } = await supabase
      .from('folders')
      .select(`
        id, 
        name, 
        color, 
        icon,
        cover_photo_id,
        photos:cover_photo_id(url)
      `)
      .eq('organization_id', organizationId)
      .is('parent_id', null) // Only top-level folders (albums)
      .order('name');
    
    if (foldersData) {
      // Get photo counts for each folder
      const folderCounts = await Promise.all(
        foldersData.map(async (folder) => {
          const { count } = await supabase
            .from('photo_folders')
            .select('*', { count: 'exact', head: true })
            .eq('folder_id', folder.id);
          
          return { 
            ...folder, 
            photoCount: count || 0,
            cover_url: (folder.photos as any)?.url || null,
            isExpanded: false,
            parentId: null
          };
        })
      );
      
      setFolders(folderCounts as Folder[]);
      setAvailableFolders(foldersData.map(f => ({ id: f.id, name: f.name, color: f.color })));
    }
  };

  useEffect(() => {
    if (!organizationId) return;

    const loadTags = async () => {
      const { data, error } = await supabase
        .from('tags')
        .select('id, name, color')
        .eq('organization_id', organizationId)
        .order('name');
      
      if (data) {
        setAvailableTags(data);
      }
    };

    loadTags();
    loadFoldersFromDB();
  }, [organizationId]);

  // Listen to realtime updates for processing status AND new inserts
  useEffect(() => {
    if (!user || !organizationId) return;

    const channel = supabase
      .channel('photo-processing-updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'photos',
          filter: `organization_id=eq.${organizationId}`
        },
        (payload) => {
          const newPhoto = payload.new as any;

          // If a dialog/alert is open, buffer inserts to avoid flashing thumbnails
          if (suppressRealtimeInsertsRef.current) {
            pendingRealtimeInsertsRef.current.push(newPhoto);
            return;
          }
          
          setPhotos(prev => {
            if (prev.some(p => p.id === newPhoto.id)) return prev;
            const mapped = {
              id: newPhoto.id,
              filename: newPhoto.filename,
              url: newPhoto.url,
              tags: [],
              description: newPhoto.description || '',
              uploadDate: newPhoto.created_at,
              updatedAt: newPhoto.updated_at,
              fileSize: newPhoto.file_size,
              fileType: newPhoto.file_type,
              dimensions: { width: newPhoto.width || 0, height: newPhoto.height || 0 },
              colors: [],
              rating: newPhoto.rating || 0,
              is_favorite: newPhoto.is_favorite ?? false,
              processingStatus: newPhoto.processing_status || 'pending',
              has_pii: newPhoto.has_pii ?? false,
              pii_details: newPhoto.pii_details ?? null,
              deleted_at: newPhoto.deleted_at ?? null,
            } as Photo;
            return [mapped, ...prev];
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'photos',
          filter: `organization_id=eq.${organizationId}`
        },
        async (payload) => {
          const updatedPhoto = payload.new as any;
          
          // Update processing files status
          setProcessingFiles(prev => 
            prev.map(f => {
              if (f.id === updatedPhoto.id) {
                if (updatedPhoto.processing_status === 'complete') {
                  return { ...f, status: 'complete' };
                } else if (updatedPhoto.processing_status === 'failed') {
                  return { ...f, status: 'failed' };
                } else if (updatedPhoto.processing_status === 'processing') {
                  return { ...f, status: 'processing' };
                }
              }
              return f;
            })
          );

          // Update photos list with fresh data
          setPhotos(prev => 
            prev.map(p => 
              p.id === updatedPhoto.id 
                ? {
                    ...p,
                    description: updatedPhoto.description || p.description,
                    processingStatus: updatedPhoto.processing_status,
                    has_pii: (typeof updatedPhoto.has_pii === 'boolean') ? updatedPhoto.has_pii : p.has_pii,
                    pii_details: (updatedPhoto.pii_details ?? p.pii_details) as any,
                    deleted_at: (updatedPhoto.deleted_at ?? p.deleted_at) as any,
                    is_favorite: (typeof updatedPhoto.is_favorite === 'boolean') ? updatedPhoto.is_favorite : (p as any).is_favorite,
                  }
                : p
            )
          );

          // When processing completes, fetch and attach colors for that photo
          if (updatedPhoto.processing_status === 'complete') {
            const { data: photoColorsData } = await supabase
              .from('photo_colors')
              .select(`
                position,
                colors (id, hex, r, g, b, ok_l, ok_a, ok_b)
              `)
              .eq('photo_id', updatedPhoto.id)
              .order('position');

            if (photoColorsData) {
              const colors = (photoColorsData as any[]).map(pc => pc.colors);
              setPhotos(prev => prev.map(p => p.id === updatedPhoto.id ? { ...p, colors } : p));
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, organizationId]);

  // Realtime: update photo colors when color links are inserted
  useEffect(() => {
    if (!user) return;

    // Debounce fetches per photoId to avoid multiple queries when multiple colors are inserted
    const timers = new Map<string, number>();

    const channel = supabase
      .channel('photo-color-updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'photo_colors',
        },
        async (payload) => {
          const photoId = (payload.new as any)?.photo_id as string | undefined;
          if (!photoId) return;

          // Only update if this photo exists in the current list
          const exists = photos.some(p => p.id === photoId);
          if (!exists) return;

          // Debounce per photoId
          const key = photoId;
          if (timers.has(key)) {
            window.clearTimeout(timers.get(key)!);
          }
          const timeoutId = window.setTimeout(async () => {
            // Fetch full color list for this photo
            const { data: photoColorsData } = await supabase
              .from('photo_colors')
              .select(`
                position,
                colors (id, hex, r, g, b, ok_l, ok_a, ok_b)
              `)
              .eq('photo_id', photoId)
              .order('position');

            if (!photoColorsData) return;

            const colors = (photoColorsData as any[]).map((pc) => pc.colors);
            setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, colors } : p));
          }, 300);
          timers.set(key, timeoutId);
        }
      )
      .subscribe();

    return () => {
      // Clear any pending timers
      timers.forEach((id) => window.clearTimeout(id));
      timers.clear();
      supabase.removeChannel(channel);
    };
  }, [user, photos.map(p => p.id).join(',')]);


  // When duplicate dialog/alerts are closed, flush any buffered realtime inserts
  useEffect(() => {
    suppressRealtimeInsertsRef.current = showDuplicateDialog;
    if (!showDuplicateDialog && pendingRealtimeInsertsRef.current.length > 0) {
      const buffered = pendingRealtimeInsertsRef.current;
      pendingRealtimeInsertsRef.current = [];
      debug.log('[realtime] flushing buffered inserts', { count: buffered.length });
      setPhotos(prev => {
        const existing = new Set(prev.map(p => p.id));
        const mapped = buffered
          .filter((p: any) => !existing.has(p.id))
          .map((p: any) => ({
            id: p.id,
            filename: p.filename,
            url: p.url,
            tags: [],
            description: p.description || '',
            uploadDate: p.created_at,
            updatedAt: p.updated_at,
            fileSize: p.file_size,
            fileType: p.file_type,
            dimensions: { width: p.width || 0, height: p.height || 0 },
            colors: [],
            rating: p.rating || 0,
            processingStatus: p.processing_status || 'pending',
            has_pii: p.has_pii ?? false,
            pii_details: p.pii_details ?? null,
            deleted_at: p.deleted_at ?? null,
          } as Photo));
        return [...mapped, ...prev];
      });
    }
  }, [showDuplicateDialog]);

  // Remove completed/failed files after a delay and auto-close panel
  useEffect(() => {
    // Do not auto-hide or prune while paused; keep panel visible for resume
    if (isUploadPaused) {
      setShowProcessingPanel(true);
      return;
    }

    // Remove completed and failed files after 2 seconds
    const completedOrFailed = processingFiles.filter(
      f => f.status === 'complete' || f.status === 'failed'
    );

    if (completedOrFailed.length > 0) {
      const removeTimer = setTimeout(() => {
        setProcessingFiles(prev => 
          prev.filter(f => f.status !== 'complete' && f.status !== 'failed')
        );
      }, 2000);

      return () => clearTimeout(removeTimer);
    }

    // Check if all files are done (no active uploads/processing)
    const hasActiveFiles = processingFiles.some(
      f => f.status === 'uploading' || f.status === 'processing'
    );

    if (!hasActiveFiles && processingFiles.every(f => f.status === 'cancelled')) {
      // All files are cancelled, close immediately
      setShowProcessingPanel(false);
      setProcessingFiles([]);
    }
  }, [processingFiles, isUploadPaused]);

  // Fallback polling: ensure processing statuses update even if realtime misses
  // IMPORTANT: Skip polling entirely during active uploads to prevent resource exhaustion
  useEffect(() => {
    // Don't poll if any files are still uploading
    const hasUploading = processingFiles.some(f => f.status === 'uploading');
    if (hasUploading) return;

    const ids = processingFiles
      .filter(f => f.status === 'processing')
      .map(f => f.id);

    if (ids.length === 0) return;

    let cancelled = false;

    const check = async () => {
      const { data, error } = await supabase
        .from('photos')
        .select('id, processing_status, description')
        .in('id', ids);

      if (cancelled || error || !data) return;

      setProcessingFiles(prev => prev.map(f => {
        const row = data.find(d => d.id === f.id);
        if (!row) return f;
        if (row.processing_status === 'complete') return { ...f, status: 'complete' };
        if (row.processing_status === 'failed') return { ...f, status: 'failed' };
        return f;
      }));

      setPhotos(prev => prev.map(p => {
        const row = data.find(d => d.id === p.id);
        return row ? {
          ...p,
          ...(row.processing_status ? ({ processingStatus: row.processing_status } as any) : {}),
          description: row.description ?? p.description,
          has_pii: (typeof (row as any).has_pii === 'boolean') ? (row as any).has_pii : p.has_pii,
          pii_details: ((row as any).pii_details ?? p.pii_details) as any,
        } : p;
      }));
    };

    check();
    const interval = setInterval(check, 5000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processingFiles.filter(f => f.status === 'processing').map(f => f.id).sort().join(',')]);

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error('Failed to sign out');
    } else {
      toast.success('Signed out successfully');
      navigate('/auth');
    }
  };

  const handleReprocessFailed = async () => {
    try {
      // Reset progress but don't open dialog until we know there's work to do
      setReprocessProgress({ total: 0, processed: 0, successful: 0, failed: 0, currentPhoto: '' });

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reprocess-failed`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(error?.error || 'Failed to reprocess photos');
      }

      const contentType = response.headers.get('content-type') || '';

      // Handle "no work" case where the function returns JSON instead of SSE
      if (!contentType.includes('text/event-stream')) {
        const json = await response.json().catch(() => null);
        if (json?.count === 0) {
          toast.info(json?.message || 'No failed or stuck photos to reprocess');
        } else if (json?.message) {
          toast.info(json.message);
        }
        setIsReprocessing(false);
        return;
      }

      // We have an SSE stream – show the dialog and stream updates
      setIsReprocessing(true);

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response body');
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.slice(6));
            
            if (data.type === 'start') {
              setReprocessProgress(prev => ({ ...prev, total: data.total }));
            } else if (data.type === 'progress') {
              setReprocessProgress(prev => ({
                total: prev.total,
                processed: data.processed,
                successful: data.successful,
                failed: data.failed,
                currentPhoto: data.currentPhoto,
              }));
            } else if (data.type === 'complete') {
              toast.success(`Reprocessed ${data.successful} photos, ${data.failed} failed`);
              // Refresh photos
              const result = await loadPhotos({
                page: currentPage,
                itemsPerPage,
                search: searchQuery,
                tagFilter: selectedTags,
                folderFilter: selectedFolderFilters,
                ratingFilter: ratingFilter,
                photoTypeFilter: photoTypeFilter,
                favoriteFilter: favoriteFilter,
                showTrash: showTrash,
                folderId: selectedFolderId,
                sortField,
                sortDirection,
              });
              setPhotos(result.photos);
            }
          }
        }
      }
    } catch (error) {
      console.error('Reprocess error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to reprocess photos');
    } finally {
      setIsReprocessing(false);
    }
  };

  const handleReprocessAll = async () => {
    try {
      shouldStopReprocessingRef.current = false;
      setIsReprocessing(true);
      setReprocessProgress({ total: 0, processed: 0, successful: 0, failed: 0, currentPhoto: '' });

      // Get all photos in organization that need embeddings
      const { data: allPhotos, error } = await supabase
        .from('photos')
        .select('id, url, filename, embedding')
        .eq('organization_id', organizationId)
        .is('deleted_at', null)
        .is('embedding', null);

      if (error) throw error;
      if (!allPhotos || allPhotos.length === 0) {
        toast.info('No photos found');
        setIsReprocessing(false);
        return;
      }

      // Extra safety: client-side filter in case backend returns extra rows
      const photosToProcess = (allPhotos || []).filter(p => !p.embedding);
      
      if (photosToProcess.length === 0) {
        toast.info('All photos already have embeddings');
        setIsReprocessing(false);
        return;
      }

      setReprocessProgress(prev => ({ ...prev, total: photosToProcess.length }));

      let successful = 0;
      let failed = 0;

      for (let i = 0; i < photosToProcess.length; i++) {
        // Check if user requested to stop using ref
        if (shouldStopReprocessingRef.current) {
          toast.info(`Stopped at ${i}/${photosToProcess.length}. Processed ${successful} successfully, ${failed} failed`);
          break;
        }

        const photo = photosToProcess[i];
        
        setReprocessProgress({
          total: photosToProcess.length,
          processed: i,
          successful,
          failed,
          currentPhoto: photo.filename,
        });

        try {
          const embedding = await generateImageEmbedding(photo.url);
          
          const { error: updateError } = await supabase
            .from('photos')
            .update({ 
              embedding: embedding as any,
              processing_status: 'complete'
            })
            .eq('id', photo.id);

          if (updateError) throw updateError;

          // Extract colors in background
          supabase.functions.invoke('extract-colors', {
            body: { photoId: photo.id, imageUrl: photo.url }
          }).catch(err => console.error('Color extraction failed:', err));
          
          successful++;
        } catch (error) {
          failed++;
          console.error(`[${i + 1}/${photosToProcess.length}] ✗ ${photo.filename}:`, error);
          
          await supabase
            .from('photos')
            .update({ processing_status: 'failed' })
            .eq('id', photo.id);
        }

        // Update final progress
        setReprocessProgress({
          total: photosToProcess.length,
          processed: i + 1,
          successful,
          failed,
          currentPhoto: '',
        });

        // Small delay to avoid overwhelming browser
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      toast.success(`Reprocessed ${successful} photos with CLIP embeddings, ${failed} failed`);
      
      // Refresh photos
              const result = await loadPhotos({
                page: currentPage,
                itemsPerPage,
                search: searchQuery,
                tagFilter: selectedTags,
                folderFilter: selectedFolderFilters,
                ratingFilter: ratingFilter,
                photoTypeFilter: photoTypeFilter,
                favoriteFilter: favoriteFilter,
                showTrash: showTrash,
                folderId: selectedFolderId,
                sortField,
                sortDirection,
              });
              setPhotos(result.photos);

    } catch (error) {
      console.error('Reprocess all error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to reprocess photos');
    } finally {
      setIsReprocessing(false);
    }
  };

  const [totalCount, setTotalCount] = useState(0);
  const [trashCount, setTrashCount] = useState(0);
  const [totalPhotosCount, setTotalPhotosCount] = useState(0); // Total non-deleted photos
  const [piiCount, setPiiCount] = useState(0);
  const [noTagsCount, setNoTagsCount] = useState(0);
  const [noFoldersCount, setNoFoldersCount] = useState(0);
  const [hasCodeCount, setHasCodeCount] = useState(0);
  const [hasPersonCount, setHasPersonCount] = useState(0);
  const [illustratedCount, setIllustratedCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);

  // Calculate items per page based on thumbnail size
  const getItemsPerPage = (size: 'small' | 'medium' | 'large') => {
    const mapping: Record<'small' | 'medium' | 'large', number> = {
      small: 100,  // Max items for small thumbnails
      medium: 60,  // Medium amount
      large: 40,   // Fewer items for large thumbnails
    };
    return mapping[size] || 60;
  };

  const itemsPerPage = getItemsPerPage(thumbnailSize);
  
  // Handle thumbnail size changes - sync with URL and clear selections
  useEffect(() => {
    const currentUrlSize = searchParams.get('size') || 'medium';
    if (thumbnailSize !== currentUrlSize) {
      // Update URL parameter
      const newParams = new URLSearchParams(searchParams);
      if (thumbnailSize === 'medium') {
        newParams.delete('size');
      } else {
        newParams.set('size', thumbnailSize);
      }
      setSearchParams(newParams);
      
      // Clear selections when size changes
      setSelectedPhotoIds([]);
    }
  }, [thumbnailSize]);

  // Calculate total pages
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  // Reload photos when filters change (not just URL string)
  useEffect(() => {
    const reloadPhotos = async () => {
      const result = await loadPhotos({
        page: currentPage,
        itemsPerPage,
        search: searchQuery,
        tagFilter: selectedTags,
        folderFilter: selectedFolderFilters,
        colorFilter: selectedColors,
        includeSimilarColors: includeSimilarColors,
        ratingFilter: ratingFilter,
        photoTypeFilter: photoTypeFilter,
        favoriteFilter: favoriteFilter,
        showTrash,
        folderId: selectedFolderId,
        sortField,
        sortDirection,
      });
      setTotalCount(result.totalCount);
    };

    reloadPhotos();
  }, [
    currentPage,
    itemsPerPage,
    searchQuery,
    JSON.stringify(selectedTags),
    JSON.stringify(selectedFolderFilters),
    JSON.stringify(selectedColors),
    includeSimilarColors,
    JSON.stringify(ratingFilter),
    photoTypeFilter,
    favoriteFilter,
    showTrash,
    selectedFolderId,
    sortField,
    sortDirection,
    user,
  ]);

  // Load counts separately (debounced to prevent excessive calls)
  useEffect(() => {
    if (!user || !organizationId) return;

    const loadCounts = async () => {
      // Load trash count
      const { count: trash } = await supabase
        .from('photos')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', organizationId)
        .not('deleted_at', 'is', null);

      setTrashCount(trash || 0);

      // Load total photos count (non-deleted)
      const { count: total } = await supabase
        .from('photos')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', organizationId)
        .is('deleted_at', null);

      setTotalPhotosCount(total || 0);

      // Load PII count (non-deleted photos with PII)
      const { count: pii } = await supabase
        .from('photos')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', organizationId)
        .is('deleted_at', null)
        .eq('has_pii', true);

      setPiiCount(pii || 0);

      // Load no tags count (active photos only) via backend function
      const { data: untaggedCount, error: noTagsError } = await supabase
        .rpc('count_photos_without_tags', { org_id: organizationId, show_trash: false });

      if (noTagsError) {
        console.error('Error loading no-tags count:', noTagsError);
        setNoTagsCount(0);
      } else {
        setNoTagsCount(Number(untaggedCount) || 0);
      }

      // Load no folders count (active photos only) via backend function
      const { data: unassignedCount, error: noFoldersError } = await supabase
        .rpc('count_photos_without_folders', { org_id: organizationId, show_trash: false });

      if (noFoldersError) {
        console.error('Error loading no-folders count:', noFoldersError);
        setNoFoldersCount(0);
      } else {
        setNoFoldersCount(Number(unassignedCount) || 0);
      }

      // Load AI detection counts
      const { data: codeCount, error: codeError } = await supabase
        .rpc('count_photos_with_code', { org_id: organizationId, show_trash: false });
      if (codeError) {
        console.error('Error loading code count:', codeError);
        setHasCodeCount(0);
      } else {
        setHasCodeCount(Number(codeCount) || 0);
      }

      const { data: personCount, error: personError } = await supabase
        .rpc('count_photos_with_person', { org_id: organizationId, show_trash: false });
      if (personError) {
        console.error('Error loading person count:', personError);
        setHasPersonCount(0);
      } else {
        setHasPersonCount(Number(personCount) || 0);
      }

      const { data: illustrCount, error: illustrError } = await supabase
        .rpc('count_photos_illustrated', { org_id: organizationId, show_trash: false });
      if (illustrError) {
        console.error('Error loading illustrated count:', illustrError);
        setIllustratedCount(0);
      } else {
        setIllustratedCount(Number(illustrCount) || 0);
      }

      const { data: errCount, error: errError } = await supabase
        .rpc('count_photos_with_error', { org_id: organizationId, show_trash: false });
      if (errError) {
        console.error('Error loading error count:', errError);
        setErrorCount(0);
      } else {
        setErrorCount(Number(errCount) || 0);
      }

      // Load total storage: call backend to sum Storage folder, fallback to DB internally
      let totalBytes = photos.reduce((sum, p) => sum + (p.fileSize || 0), 0);
      try {
        if (organizationId) {
          console.log('Calling get-storage-usage for org:', organizationId);
          const { data, error } = await supabase.functions.invoke('get-storage-usage', {
            body: { organizationId },
          });
          console.log('get-storage-usage response:', { data, error });
          if (error) {
            console.error('Storage usage error:', error);
          }
          if (!error && data && typeof data.totalBytes === 'number') {
            totalBytes = data.totalBytes;
          }
        }
      } catch (e) {
        console.error('Exception calling get-storage-usage:', e);
      }
      setTotalStorageBytes(totalBytes);
    };

    // Debounce to prevent excessive calls
    const timeoutId = setTimeout(loadCounts, 500);
    return () => clearTimeout(timeoutId);
  }, [user, organizationId, photos.length]); // Only reload when photo count changes, not on every photo update

  // Refresh counts and photos when reprocess completes/stops
  useEffect(() => {
    if (!user || !organizationId) return;

    const handler = async () => {
      // Reload counts
      const { count: trash } = await supabase
        .from('photos')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', organizationId)
        .not('deleted_at', 'is', null);
      setTrashCount(trash || 0);

      const { count: total } = await supabase
        .from('photos')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', organizationId)
        .is('deleted_at', null);
      setTotalPhotosCount(total || 0);

      const { count: pii } = await supabase
        .from('photos')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', organizationId)
        .is('deleted_at', null)
        .eq('has_pii', true);
      setPiiCount(pii || 0);

      const { data: untaggedCount } = await supabase
        .rpc('count_photos_without_tags', { org_id: organizationId, show_trash: false });
      setNoTagsCount(Number(untaggedCount) || 0);

      const { data: unassignedCount } = await supabase
        .rpc('count_photos_without_folders', { org_id: organizationId, show_trash: false });
      setNoFoldersCount(Number(unassignedCount) || 0);

      // If currently viewing "no tags" or "no folders", reload photos
      if (photoTypeFilter === 'no_tags' || photoTypeFilter === 'no_folders') {
        const result = await loadPhotos({
          page: currentPage,
          itemsPerPage,
          search: searchQuery,
          tagFilter: selectedTags,
          folderFilter: selectedFolderFilters,
          colorFilter: selectedColors,
          includeSimilarColors,
          ratingFilter,
          photoTypeFilter,
          favoriteFilter,
          showTrash,
          folderId: selectedFolderId,
          sortField,
          sortDirection,
        });
        setTotalCount(result.totalCount);
      }
    };

    const onComplete = () => { handler(); };
    const onStopped = () => { handler(); };

    window.addEventListener('reprocess-no-tags:complete', onComplete);
    window.addEventListener('reprocess-no-tags:stopped', onStopped);
    return () => {
      window.removeEventListener('reprocess-no-tags:complete', onComplete);
      window.removeEventListener('reprocess-no-tags:stopped', onStopped);
    };
  }, [user, organizationId, photoTypeFilter, currentPage, itemsPerPage, searchQuery, JSON.stringify(selectedTags), JSON.stringify(selectedFolderFilters), JSON.stringify(selectedColors), includeSimilarColors, JSON.stringify(ratingFilter), favoriteFilter, showTrash, selectedFolderId, sortField, sortDirection]);

  // Reset to page 1 when filters change (derived from URL)
  const prevFiltersRef = useRef<string>('');
  useEffect(() => {
    const filtersStr = `${searchQuery}|${JSON.stringify(selectedTags)}|${JSON.stringify(selectedFolderFilters)}|${JSON.stringify(ratingFilter)}|${photoTypeFilter}|${selectedFolderId}|${showTrash}|${sortField}|${sortDirection}`;
    if (prevFiltersRef.current && prevFiltersRef.current !== filtersStr && currentPage !== 1) {
      updateFilters({ page: 1 });
    }
    prevFiltersRef.current = filtersStr;
  }, [searchQuery, JSON.stringify(selectedTags), JSON.stringify(selectedFolderFilters), JSON.stringify(ratingFilter), photoTypeFilter, selectedFolderId, showTrash, sortField, sortDirection, currentPage]);

  const selectedPhoto = photos.find(p => p.id === selectedPhotoId) || null;

  const getCurrentFolderName = () => {
    if (showTrash) return 'Trash';
    if (photoTypeFilter === 'has_pii') return 'All Photos - Has PII';
    if (photoTypeFilter === 'no_tags') return 'All Photos - No Tags';
    if (photoTypeFilter === 'no_folders') return 'All Photos - No Folders';
    if (photoTypeFilter === 'has_code') return 'All Photos - Code';
    if (photoTypeFilter === 'has_person') return 'All Photos - Person';
    if (photoTypeFilter === 'is_illustrated') return 'All Photos - Illustrated';
    if (photoTypeFilter === 'has_error') return 'All Photos - Error';
    if (!selectedFolderId) return 'All Photos';
    const folder = folders.find(f => f.id === selectedFolderId);
    return folder?.name || 'All Photos';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files).filter(file =>
      file.type.startsWith('image/') ||
      file.name.toLowerCase().endsWith('.heic') ||
      file.name.toLowerCase().endsWith('.heif')
    );

    if (files.length === 0 || !user) {
      toast.error('Please drop image files only');
      return;
    }

    // Get user's first organization
    const { data: userOrgs } = await supabase
      .from('user_organizations')
      .select('organization_id')
      .eq('user_id', user.id)
      .limit(1);

    if (!userOrgs || userOrgs.length === 0) {
      console.error('No organization found for user');
      toast.error('No organization found. Please create an organization first.');
      return;
    }

    const organizationId = userOrgs[0].organization_id;

    // If skip all is enabled, we'll handle skipping duplicates automatically
    // after performing the duplicate check below.

    // Duplicate check in batches to avoid URL length limits
    const BATCH_SIZE = 50; // Check 50 filenames at a time
    const filenames = files.map(f => f.name);
    const existingFilenames = new Set<string>();
    
    for (let i = 0; i < filenames.length; i += BATCH_SIZE) {
      const batch = filenames.slice(i, i + BATCH_SIZE);
      const { data: batchExisting, error } = await supabase
        .from('photos')
        .select('filename')
        .eq('organization_id', organizationId)
        .is('deleted_at', null)
        .in('filename', batch);
      
      if (error) {
        console.error('Error checking duplicates:', error);
        continue;
      }
      
      batchExisting?.forEach(p => existingFilenames.add(p.filename));
    }

    const duplicates = files.filter(f => existingFilenames.has(f.name));
    const nonDuplicates = files.filter(f => !existingFilenames.has(f.name));

    debug.log('Duplicate check (drop):', {
      totalFiles: files.length,
      duplicates: duplicates.length,
      nonDuplicates: nonDuplicates.length,
      duplicateNames: duplicates.map(f => f.name)
    });

    // If ALL files are duplicates, show dialog immediately
    if (duplicates.length > 0 && nonDuplicates.length === 0) {
      setDuplicateFiles(duplicates);
      setDuplicateFilenames(duplicates.map(f => f.name));
      setShowDuplicateDialog(true);
      return;
    }

    // Decide on duplicates BEFORE uploading anything
    if (duplicates.length > 0) {
      if (!skipAllDuplicates) {
        // Open dialog and wait for user's choice
        pendingNonDuplicatesRef.current = nonDuplicates;
        pendingDuplicatesRef.current = duplicates;
        debug.log('[duplicates] opening dialog', { duplicates: duplicates.length, nonDuplicates: nonDuplicates.length });
        setDuplicateFiles(duplicates);
        setDuplicateFilenames(duplicates.map(f => f.name));
        setShowDuplicateDialog(true);
        return;
      } else {
        // Auto-skip duplicates
        toast.info(`Skipped ${duplicates.length} duplicate file${duplicates.length > 1 ? 's' : ''}`);
      }
    }

    // Now upload non-duplicates (if any)
    if (nonDuplicates.length > 0) {
      setShowProcessingPanel(true);
      const newProcessingFiles: ProcessingFile[] = nonDuplicates.map(file => ({
        id: crypto.randomUUID(),
        filename: file.name,
        status: 'uploading',
        progress: 0,
      }));
      setProcessingFiles(prev => [...prev, ...newProcessingFiles]);
      setUploadQueue(nonDuplicates);

      toast.info(`Uploading ${nonDuplicates.length} photo${nonDuplicates.length > 1 ? 's' : ''}...`);

      await processUploadQueue(nonDuplicates, newProcessingFiles);
      setUploadQueue([]);

      // Refresh photos from database
      const { data: newPhotos } = await supabase
        .from('photos')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });
        
      if (newPhotos) {
        const { data: photoTagsData } = await supabase
          .from('photo_tags')
          .select('photo_id, tag_id');

        const photoTagsMap = new Map<string, string[]>();
        photoTagsData?.forEach((pt: any) => {
          const existing = photoTagsMap.get(pt.photo_id) || [];
          photoTagsMap.set(pt.photo_id, [...existing, pt.tag_id]);
        });

        // Generate signed URLs for new photos
        const newPhotosWithSignedUrls = await addSignedUrlsToPhotos(newPhotos);
        
        const mappedPhotos = newPhotosWithSignedUrls.map(p => ({
          id: p.id,
          filename: p.filename,
          url: p.url,
          tags: [],
          tagIds: photoTagsMap.get(p.id) || [],
          description: p.description || '',
          uploadDate: p.created_at,
          updatedAt: p.updated_at,
          fileSize: p.file_size,
          fileType: p.file_type,
          dimensions: { width: p.width || 0, height: p.height || 0 },
          colors: [],
          rating: p.rating || 0,
          processingStatus: (p.processing_status || 'pending') as 'pending' | 'processing' | 'complete' | 'failed',
          has_pii: p.has_pii ?? false,
          pii_details: p.pii_details ?? null,
          deleted_at: p.deleted_at ?? null,
        }));
        
        setPhotos(mappedPhotos);
      }
    }
  };

  const handleToggleFolder = (folderId: string) => {
    setFolders(prev =>
      prev.map(f =>
        f.id === folderId ? { ...f, isExpanded: !f.isExpanded } : f
      )
    );
  };

  const handleShowAll = () => {
    updateFilters({ 
      trash: false, 
      type: 'all', 
      tags: [],
      folders: [],
      colors: [],
      rating: null,
      favorite: false,
      search: ''
    });
    setSelectedFolderId(null);
  };

  const handleShowTrash = () => {
    updateFilters({ 
      trash: true, 
      type: 'all', 
      tags: [],
      folders: [],
      colors: [],
      rating: null,
      favorite: false,
      search: ''
    });
    setSelectedFolderId(null);
  };

  const handleShowPII = () => {
    updateFilters({ 
      trash: false, 
      type: 'has_pii', 
      tags: [],
      folders: [],
      colors: [],
      rating: null,
      favorite: false,
      search: ''
    });
    setSelectedFolderId(null);
  };

  const handleShowNoTags = () => {
    updateFilters({ 
      trash: false, 
      type: 'no_tags', 
      tags: [], 
      folders: [],
      colors: [],
      rating: null,
      favorite: false,
      search: ''
    });
    setSelectedFolderId(null);
  };

  const handleShowNoFolders = () => {
    updateFilters({ 
      trash: false, 
      type: 'no_folders', 
      folders: [], 
      tags: [],
      colors: [],
      rating: null,
      favorite: false,
      search: ''
    });
    setSelectedFolderId(null);
  };

  const handleShowHasCode = () => {
    updateFilters({ 
      trash: false, 
      type: 'has_code', 
      tags: [],
      folders: [],
      colors: [],
      rating: null,
      favorite: false,
      search: ''
    });
    setSelectedFolderId(null);
  };

  const handleShowHasPerson = () => {
    updateFilters({ 
      trash: false, 
      type: 'has_person', 
      tags: [],
      folders: [],
      colors: [],
      rating: null,
      favorite: false,
      search: ''
    });
    setSelectedFolderId(null);
  };

  const handleShowIllustrated = () => {
    updateFilters({ 
      trash: false, 
      type: 'is_illustrated', 
      tags: [],
      folders: [],
      colors: [],
      rating: null,
      favorite: false,
      search: ''
    });
    setSelectedFolderId(null);
  };

  const handleShowError = () => {
    updateFilters({ 
      trash: false, 
      type: 'has_error', 
      tags: [],
      folders: [],
      colors: [],
      rating: null,
      favorite: false,
      search: ''
    });
    setSelectedFolderId(null);
  };

  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter(file =>
      file.type.startsWith('image/') ||
      file.name.toLowerCase().endsWith('.heic') ||
      file.name.toLowerCase().endsWith('.heif')
    );

    if (files.length === 0 || !user) {
      toast.error('Please select image files only');
      return;
    }

    // Get user's first organization
    const { data: userOrgs } = await supabase
      .from('user_organizations')
      .select('organization_id')
      .eq('user_id', user.id)
      .limit(1);

    if (!userOrgs || userOrgs.length === 0) {
      toast.error('No organization found. Please create an organization first.');
      return;
    }

    const organizationId = userOrgs[0].organization_id;

    // Get organization's photo limit
    const { data: orgData } = await supabase
      .from('organizations')
      .select('photo_limit, per_upload_limit')
      .eq('id', organizationId)
      .single();

    const photoLimit = orgData?.photo_limit || 10000;
    const perUploadLimit = orgData?.per_upload_limit || 250;
    setPhotoLimitForOrg(photoLimit);
    setPerUploadLimit(perUploadLimit);

    // Check per-upload limit first
    if (files.length > perUploadLimit) {
      toast.error(`Cannot upload ${files.length} photos at once. Maximum ${perUploadLimit} photos per upload.`);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Check current photo count
    const { count: currentPhotoCount } = await supabase
      .from('photos')
      .select('*', { count: 'exact', head: true })
      .eq('organization_id', organizationId)
      .is('deleted_at', null);

    const totalAfterUpload = (currentPhotoCount || 0) + files.length;

    if (totalAfterUpload > photoLimit) {
      const remainingSlots = photoLimit - (currentPhotoCount || 0);
      toast.error(
        remainingSlots > 0
          ? `Cannot upload ${files.length} photo${files.length > 1 ? 's' : ''}. You currently have ${currentPhotoCount} photo${currentPhotoCount === 1 ? '' : 's'} and can store up to ${photoLimit} total. Only ${remainingSlots} slot${remainingSlots === 1 ? '' : 's'} remaining.`
          : `Cannot upload. You have ${currentPhotoCount} photos stored and have reached your ${photoLimit} photo storage limit. Please delete some photos to upload more.`
      );
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // If skip-all is enabled, we'll still perform duplicate checking
    // and automatically skip duplicates without showing the dialog.

    // Check for duplicate filenames in batches to avoid URL length limits
    const BATCH_SIZE = 50;
    const filenames = files.map(f => f.name);
    const existingFilenames = new Set<string>();
    
    for (let i = 0; i < filenames.length; i += BATCH_SIZE) {
      const batch = filenames.slice(i, i + BATCH_SIZE);
      const { data: batchExisting, error } = await supabase
        .from('photos')
        .select('filename')
        .eq('organization_id', organizationId)
        .is('deleted_at', null)
        .in('filename', batch);
      
      if (error) {
        console.error('Error checking duplicates:', error);
        continue;
      }
      
      batchExisting?.forEach(p => existingFilenames.add(p.filename));
    }

    const duplicates = files.filter(f => existingFilenames.has(f.name));
    const nonDuplicates = files.filter(f => !existingFilenames.has(f.name));

    debug.log('Duplicate check:', { 
      totalFiles: files.length, 
      duplicates: duplicates.length, 
      nonDuplicates: nonDuplicates.length,
      duplicateNames: duplicates.map(f => f.name)
    });

    // If ALL files are duplicates, show dialog immediately
    if (duplicates.length > 0 && nonDuplicates.length === 0) {
      setDuplicateFiles(duplicates);
      setDuplicateFilenames(duplicates.map(f => f.name));
      setShowDuplicateDialog(true);
      return;
    }

    // Show processing panel
    setShowProcessingPanel(true);

    // Process non-duplicates first
    if (nonDuplicates.length > 0) {
      const newProcessingFiles: ProcessingFile[] = nonDuplicates.map(file => ({
        id: crypto.randomUUID(),
        filename: file.name,
        status: 'uploading',
        progress: 0,
      }));
      setProcessingFiles(prev => [...prev, ...newProcessingFiles]);
      setUploadQueue(nonDuplicates);

      toast.info(`Uploading ${nonDuplicates.length} photo${nonDuplicates.length > 1 ? 's' : ''}...`);

      await processUploadQueue(nonDuplicates, newProcessingFiles);
      setUploadQueue([]);

      // Refresh photos after processing non-duplicates
      const { data: newPhotos } = await supabase
        .from('photos')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });
        
      if (newPhotos) {
        const { data: photoTagsData } = await supabase
          .from('photo_tags')
          .select('photo_id, tag_id');

        const photoTagsMap = new Map<string, string[]>();
        photoTagsData?.forEach((pt: any) => {
          const existing = photoTagsMap.get(pt.photo_id) || [];
          photoTagsMap.set(pt.photo_id, [...existing, pt.tag_id]);
        });

        // Generate signed URLs for folder photos
        const folderPhotosWithSignedUrls = await addSignedUrlsToPhotos(newPhotos);
        
        const mappedPhotos = folderPhotosWithSignedUrls.map(p => ({
          id: p.id,
          filename: p.filename,
          url: p.url,
          tags: [],
          tagIds: photoTagsMap.get(p.id) || [],
          description: p.description || '',
          uploadDate: p.created_at,
          updatedAt: p.updated_at,
          fileSize: p.file_size,
          fileType: p.file_type,
          dimensions: { width: p.width || 0, height: p.height || 0 },
          colors: [],
          rating: p.rating || 0,
          processingStatus: (p.processing_status || 'pending') as 'pending' | 'processing' | 'complete' | 'failed',
          has_pii: p.has_pii ?? false,
          pii_details: p.pii_details ?? null,
          deleted_at: p.deleted_at ?? null,
        }));
        
        setPhotos(mappedPhotos);
      }

      // Add small delay before showing duplicate dialog so user sees upload completion
      if (duplicates.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 800));
      }
    }

    // Handle duplicates after non-duplicates are processed
    if (duplicates.length > 0) {
      if (skipAllDuplicates) {
        // Auto-skip duplicates without showing dialog
        setShowDuplicateDialog(false);
        setDuplicateFiles([]);
        setDuplicateFilenames([]);
        toast.info(`Skipped ${duplicates.length} duplicate file${duplicates.length > 1 ? 's' : ''}`);
        // Reset file input to allow re-selecting same files
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      setDuplicateFiles(duplicates);
      setDuplicateFilenames(duplicates.map(f => f.name));
      setShowDuplicateDialog(true);
      return;
    }

    // Only reset file input if no duplicates to handle
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePhotoToggle = (photoId: string) => {
    setSelectedPhotoIds(prev =>
      prev.includes(photoId)
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  };

  const handleSelectAll = () => {
    if (selectedPhotoIds.length === photos.length) {
      setSelectedPhotoIds([]);
    } else {
      setSelectedPhotoIds(photos.map(p => p.id));
    }
  };

  const handleSelectAllPhotos = async () => {
    if (!user) return;
    
    try {
      const { data: userOrgs } = await supabase
        .from('user_organizations')
        .select('organization_id')
        .eq('user_id', user.id)
        .limit(1);

      if (!userOrgs || userOrgs.length === 0) return;

      const orgId = userOrgs[0].organization_id;

      // Build query to get all photo IDs with current filters
      let query = supabase
        .from('photos')
        .select('id')
        .eq('organization_id', orgId);

      // Apply trash filter
      if (showTrash) {
        query = query.not('deleted_at', 'is', null);
      } else {
        query = query.is('deleted_at', null);
        
        // Folder filter using photo_folders junction table
        if (selectedFolderId) {
          const { data: photoFolderData } = await supabase
            .from('photo_folders')
            .select('photo_id')
            .eq('folder_id', selectedFolderId);
          const photoIds = photoFolderData?.map(pf => pf.photo_id) || [];
          if (photoIds.length > 0) {
            query = query.in('id', photoIds);
          } else {
            // No photos in this folder, return empty
            setPhotos([]);
            return;
          }
        }
      }

      // Apply folder filters from dropdown using photo_folders
      if (selectedFolderFilters && selectedFolderFilters.length > 0) {
        const { data: photoFolderData } = await supabase
          .from('photo_folders')
          .select('photo_id')
          .in('folder_id', selectedFolderFilters);
        const photoIds = photoFolderData?.map(pf => pf.photo_id) || [];
        if (photoIds.length > 0) {
          query = query.in('id', photoIds);
        } else {
          setPhotos([]);
          return;
        }
      }

      // Apply photo type filter
      if (photoTypeFilter === 'has_pii') {
        query = query.eq('has_pii', true);
      } else if (photoTypeFilter === 'no_tags') {
        // Get all photo IDs that don't have tags
        const { data: allPhotoIds } = await query;
        const photoIds = allPhotoIds?.map(p => p.id) || [];
        
        const { data: taggedPhotoIds } = await supabase
          .from('photo_tags')
          .select('photo_id')
          .in('photo_id', photoIds);
        
        const taggedIds = new Set(taggedPhotoIds?.map(pt => pt.photo_id) || []);
        const untaggedPhotoIds = photoIds.filter(id => !taggedIds.has(id));
        
        setSelectedPhotoIds(untaggedPhotoIds);
        toast.success(`Selected ${untaggedPhotoIds.length} photos`);
        return;
      } else if (photoTypeFilter === 'has_tag') {
        if (selectedTags && selectedTags.length > 0) {
          const { data: taggedPhotos } = await supabase
            .from('photo_tags')
            .select('photo_id')
            .in('tag_id', selectedTags);
          
          const uniquePhotoIds = Array.from(new Set(taggedPhotos?.map(pt => pt.photo_id) || []));
          if (uniquePhotoIds.length > 0) {
            query = query.in('id', uniquePhotoIds);
          } else {
            setSelectedPhotoIds([]);
            return;
          }
        } else {
          // Any photos with tags
          const { data: allTaggedPhotos } = await supabase
            .from('photo_tags')
            .select('photo_id');
          
          const uniquePhotoIds = Array.from(new Set(allTaggedPhotos?.map(pt => pt.photo_id) || []));
          if (uniquePhotoIds.length > 0) {
            query = query.in('id', uniquePhotoIds);
          } else {
            setSelectedPhotoIds([]);
            return;
          }
        }
      }

      // Apply tag filter (when not using photo type filter)
      if (photoTypeFilter !== 'has_tag' && selectedTags && selectedTags.length > 0) {
        const { data: taggedPhotos } = await supabase
          .from('photo_tags')
          .select('photo_id')
          .in('tag_id', selectedTags);
        
        const uniquePhotoIds = Array.from(new Set(taggedPhotos?.map(pt => pt.photo_id) || []));
        if (uniquePhotoIds.length > 0) {
          query = query.in('id', uniquePhotoIds);
        } else {
          setSelectedPhotoIds([]);
          return;
        }
      }

      // Apply rating filter
      if (ratingFilter) {
        const { operator, value } = ratingFilter;
        if (operator === 'gte') {
          query = query.gte('rating', value);
        } else if (operator === 'lte') {
          query = query.lte('rating', value);
        } else if (operator === 'eq') {
          query = query.eq('rating', value);
        }
      }

      // Apply search filter (if any)
      if (searchQuery && searchQuery.trim()) {
        const searchTerm = `%${searchQuery.trim()}%`;
        query = query.or(`filename.ilike.${searchTerm},description.ilike.${searchTerm}`);
      }

      const { data: allPhotos, error } = await query;

      if (error) {
        console.error('Error fetching all photos:', error);
        toast.error('Failed to select all photos');
        return;
      }

      const photoIds = allPhotos?.map(p => p.id) || [];
      setSelectedPhotoIds(photoIds);
      toast.success(`Selected ${photoIds.length} photos`);
    } catch (error) {
      console.error('Error selecting all photos:', error);
      toast.error('Failed to select all photos');
    }
  };


  const handleEmptyTrash = () => {
    if (trashCount === 0) return;
    setShowEmptyTrashDialog(true);
  };

  const handleConfirmEmptyTrash = async () => {
    if (!user) return;

    setIsEmptyingTrash(true);
    try {
      // Get organization ID first
      const { data: userOrg } = await supabase
        .from('user_organizations')
        .select('organization_id')
        .eq('user_id', user.id)
        .limit(1)
        .single();

      if (!userOrg) {
        toast.error('Organization not found');
        return;
      }

      // Get all trashed photos with their storage URLs
      const { data: trashedPhotos, error: fetchError } = await supabase
        .from('photos')
        .select('id, url')
        .not('deleted_at', 'is', null)
        .eq('organization_id', userOrg.organization_id);

      if (fetchError || !trashedPhotos) {
        toast.error('Failed to fetch trashed photos');
        console.error('Fetch trash error:', fetchError);
        return;
      }

      if (trashedPhotos.length === 0) {
        toast.info('Trash is already empty');
        setShowEmptyTrashDialog(false);
        return;
      }

      // Prepare IDs and file paths
      const ids = trashedPhotos.map((p) => p.id);
      const filePaths = trashedPhotos.map((photo) => {
        const urlParts = photo.url.split('/');
        return urlParts.slice(urlParts.indexOf('photos') + 1).join('/');
      });

      // Chunk helper
      const chunk = (arr: string[], size: number) => {
        const chunks: string[][] = [];
        for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size));
        return chunks;
      };

      const idChunks = chunk(ids, 100);

      // Delete in batches to avoid URL size limits
      for (const idChunk of idChunks) {
        const { error: ptErr } = await supabase
          .from('photo_tags')
          .delete()
          .in('photo_id', idChunk);
        if (ptErr) {
          console.error('Delete photo_tags error:', ptErr);
          toast.error('Failed to remove tags for trashed photos');
          return;
        }

        const { error: pfErr } = await supabase
          .from('photo_folders')
          .delete()
          .in('photo_id', idChunk);
        if (pfErr) {
          console.error('Delete photo_folders error:', pfErr);
          toast.error('Failed to remove folder links for trashed photos');
          return;
        }

        const { error: delErr } = await supabase
          .from('photos')
          .delete()
          .in('id', idChunk)
          .eq('organization_id', userOrg.organization_id);
        if (delErr) {
          console.error('Delete photos error:', delErr);
          toast.error('Failed to empty trash');
          return;
        }
      }

      // Delete files from storage in batches
      const pathChunks = chunk(filePaths, 100);
      for (const pathChunk of pathChunks) {
        const { error: storageErr } = await supabase.storage.from('photos').remove(pathChunk);
        if (storageErr) {
          console.warn('Storage delete warning:', storageErr);
          // Continue; not critical for DB consistency
        }
      }


      toast.success(`Permanently deleted ${trashedPhotos.length} photo(s)`);
      setShowEmptyTrashDialog(false);
      
      // Reload photos to update UI
      const result = await loadPhotos({
        page: currentPage,
        itemsPerPage,
        search: searchQuery,
        tagFilter: selectedTags,
        folderFilter: selectedFolderFilters,
        ratingFilter: ratingFilter,
        photoTypeFilter: photoTypeFilter,
        favoriteFilter: favoriteFilter,
        showTrash: showTrash,
        folderId: selectedFolderId,
      });

      if (result) {
        setPhotos(result.photos);
        setTotalCount(result.totalCount);
      }
    } catch (error) {
      console.error('Error emptying trash:', error);
      toast.error('Failed to empty trash');
    } finally {
      setIsEmptyingTrash(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPhotoIds.length === 0) return;
    setShowDeletePhotosDialog(true);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedPhotoIds.length === 0) return;

    try {
      setIsDeletingPhotos(true);
      // Soft delete: update deleted_at timestamp
      const { error } = await supabase
        .from('photos')
        .update({ deleted_at: new Date().toISOString() })
        .in('id', selectedPhotoIds);

      if (error) {
        toast.error('Failed to delete photos');
        console.error('Delete error:', error);
      } else {
        toast.success(`Moved ${selectedPhotoIds.length} photo(s) to trash`);
        setPhotos(prev => prev.filter(p => !selectedPhotoIds.includes(p.id)));
        setSelectedPhotoIds([]);
        setShowDeletePhotosDialog(false);
        
        // Reload to sync UI
        const result = await loadPhotos({
          page: currentPage,
          itemsPerPage,
          search: searchQuery,
          tagFilter: selectedTags,
          folderFilter: selectedFolderFilters,
          ratingFilter: ratingFilter,
          photoTypeFilter: photoTypeFilter,
          favoriteFilter: favoriteFilter,
          showTrash: showTrash,
          folderId: selectedFolderId,
          sortField,
          sortDirection,
        });
        setPhotos(result.photos);
      }
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Failed to delete photos');
    } finally {
      setIsDeletingPhotos(false);
    }
  };

  const handleBulkRestore = async () => {
    if (selectedPhotoIds.length === 0) return;

    try {
      const { restoreFromTrash } = await import("@/utils/photoUpload");
      
      let successCount = 0;
      let failCount = 0;

      for (const photoId of selectedPhotoIds) {
        const result = await restoreFromTrash(photoId, organizationId);
        if (result.success) {
          successCount++;
        } else {
          failCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Restored ${successCount} photo(s) from trash`);
        setPhotos(prev => prev.filter(p => !selectedPhotoIds.includes(p.id)));
        setSelectedPhotoIds([]);
        
        // Reload to sync UI
        const result = await loadPhotos({
          page: currentPage,
          itemsPerPage,
          search: searchQuery,
          tagFilter: selectedTags,
          folderFilter: selectedFolderFilters,
          ratingFilter: ratingFilter,
          photoTypeFilter: photoTypeFilter,
          favoriteFilter: favoriteFilter,
          showTrash: showTrash,
          folderId: selectedFolderId,
          sortField,
          sortDirection,
        });
        setPhotos(result.photos);
      }

      if (failCount > 0) {
        toast.error(`Failed to restore ${failCount} photo(s)`);
      }
    } catch (error) {
      console.error('Bulk restore error:', error);
      toast.error('Failed to restore photos');
    }
  };

  const handleCancelProcessing = async (fileId: string) => {
    const file = processingFiles.find(f => f.id === fileId);
    
    // If already failed or cancelled, just remove from list
    if (file && (file.status === 'failed' || file.status === 'cancelled')) {
      setProcessingFiles(prev => prev.filter(f => f.id !== fileId));
      return;
    }
    
    // Otherwise, mark as cancelled and delete from database
    setProcessingFiles(prev =>
      prev.map(f => (f.id === fileId ? { ...f, status: 'cancelled' } : f))
    );

    // Delete the photo from database if it was already created
    const { error } = await supabase
      .from('photos')
      .delete()
      .eq('id', fileId);

    if (!error) {
      setPhotos(prev => prev.filter(p => p.id !== fileId));
    }
  };

  const handleCancelAllProcessing = async () => {
    const activeFileIds = processingFiles
      .filter(f => f.status === 'uploading' || f.status === 'processing')
      .map(f => f.id);

    // Abort any in-flight uploads and reset pause state
    if (uploadAbortController.current) {
      uploadAbortController.current.abort();
    }
    setIsUploadPaused(false);
    isUploadPausedRef.current = false;
    setUploadQueue([]);

    // Mark all as cancelled
    setProcessingFiles(prev =>
      prev.map(f =>
        activeFileIds.includes(f.id) ? { ...f, status: 'cancelled', progress: 0 } : f
      )
    );

    // Delete photos from database
    if (activeFileIds.length > 0) {
      const { error } = await supabase
        .from('photos')
        .delete()
        .in('id', activeFileIds);

      if (!error) {
        setPhotos(prev => prev.filter(p => !activeFileIds.includes(p.id)));
      }
    }

    // Clear cancelled/failed entries and close the panel
    setProcessingFiles(prev => prev.filter(f => f.status !== 'failed' && f.status !== 'cancelled'));
    setShowProcessingPanel(false);

    toast.info('Cancelled all pending uploads');
  };

  const handlePauseUpload = () => {
    setIsUploadPaused(true);
    isUploadPausedRef.current = true;
    if (uploadAbortController.current) {
      uploadAbortController.current.abort();
    }
    
    // Mark currently uploading files as cancelled (paused) so they can be resumed
    setProcessingFiles(prev =>
      prev.map(f => 
        f.status === 'uploading' ? { ...f, status: 'cancelled', progress: 0 } : f
      )
    );
    
    toast.info('Upload paused');
  };

  const handleContinueUpload = async () => {
    let queue = uploadQueue;
    if (queue.length === 0) {
      // Rebuild queue from last batch using cancelled/uploading entries
      const namesToResume = new Set(
        processingFiles
          .filter(f => f.status === 'cancelled' || f.status === 'uploading')
          .map(f => f.filename)
      );
      const fallback = (lastBatchRef.current || []).filter(f => namesToResume.has(f.name));
      if (fallback.length === 0) {
        toast.error('No files in queue to resume');
        return;
      }
      setUploadQueue(fallback);
      queue = fallback;
    }
    
    setIsUploadPaused(false);
    isUploadPausedRef.current = false;
    
    // Create new abort controller for resumed upload
    uploadAbortController.current = new AbortController();
    
    // Ensure panel is visible when resuming
    setShowProcessingPanel(true);
    
    toast.info('Resuming upload...');
    
    // Build a snapshot with resumed statuses so the queue can start immediately
    const snapshot = processingFiles.map(f => {
      const isInQueue = queue.some(queueFile => queueFile.name === f.filename);
      if (isInQueue && (f.status === 'uploading' || f.status === 'cancelled')) {
        return { ...f, status: 'uploading', progress: 0 } as ProcessingFile;
      }
      return f;
    });

    // Apply snapshot to state
    setProcessingFiles(() => snapshot);
    
    // Process remaining files in queue using snapshot to avoid race condition
    await processUploadQueue(queue, snapshot);
    setUploadQueue([]);
  };

  const handleOverwriteDuplicates = async () => {
    setShowDuplicateDialog(false);
    
    const dupes = pendingDuplicatesRef.current || duplicateFiles;
    const nonDupes = pendingNonDuplicatesRef.current;
    
    pendingNonDuplicatesRef.current = null;
    pendingDuplicatesRef.current = null;
    
    if (dupes.length === 0 || !organizationId) return;

    // Add files to processing state
    const newProcessingFiles: ProcessingFile[] = dupes.map(file => ({
      id: crypto.randomUUID(),
      filename: file.name,
      status: 'uploading',
      progress: 0,
    }));
    setProcessingFiles(prev => [...prev, ...newProcessingFiles]);
    setUploadQueue(dupes);

    toast.info(`Overwriting ${dupes.length} photo${dupes.length > 1 ? 's' : ''}...`);

    // Delete existing photos with same filenames first
    await supabase
      .from('photos')
      .delete()
      .eq('organization_id', organizationId)
      .in('filename', dupes.map(f => f.name));

    // Process the duplicate files
    await processUploadQueue(dupes);
    setUploadQueue([]);
    
    // Clear duplicate state
    setDuplicateFiles([]);
    setDuplicateFilenames([]);

    // Now also upload the non-duplicates if any
    if (nonDupes && nonDupes.length > 0) {
      const nonDupeProcessingFiles: ProcessingFile[] = nonDupes.map(file => ({
        id: crypto.randomUUID(),
        filename: file.name,
        status: 'uploading',
        progress: 0,
      }));
      setProcessingFiles(prev => [...prev, ...nonDupeProcessingFiles]);
      setUploadQueue(nonDupes);
      
      toast.info(`Uploading ${nonDupes.length} additional photo${nonDupes.length > 1 ? 's' : ''}...`);
      
      await processUploadQueue(nonDupes);
      setUploadQueue([]);
    }

    // Refresh photos from database
    const { data: newPhotos } = await supabase
      .from('photos')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });
      
    if (newPhotos) {
      const { data: photoTagsData } = await supabase
        .from('photo_tags')
        .select('photo_id, tag_id');

      const photoTagsMap = new Map<string, string[]>();
      photoTagsData?.forEach((pt: any) => {
        const existing = photoTagsMap.get(pt.photo_id) || [];
        photoTagsMap.set(pt.photo_id, [...existing, pt.tag_id]);
      });

        // Generate signed URLs for search results
        const searchPhotosWithSignedUrls = await addSignedUrlsToPhotos(newPhotos);
        
        const mappedPhotos = searchPhotosWithSignedUrls.map(p => ({
          id: p.id,
          filename: p.filename,
          url: p.url,
          tags: [],
          tagIds: photoTagsMap.get(p.id) || [],
          description: p.description || '',
        uploadDate: p.created_at,
        updatedAt: p.updated_at,
        fileSize: p.file_size,
        fileType: p.file_type,
        dimensions: { width: p.width || 0, height: p.height || 0 },
        colors: [],
        rating: p.rating || 0,
        processingStatus: (p.processing_status || 'pending') as 'pending' | 'processing' | 'complete' | 'failed',
        has_pii: p.has_pii ?? false,
        pii_details: p.pii_details ?? null,
        deleted_at: p.deleted_at ?? null,
      }));

      setPhotos(mappedPhotos);
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSkipDuplicates = async () => {
    debug.log('[duplicates] skip pressed');
    setShowDuplicateDialog(false);
    const skippedCount = duplicateFiles.length;
    setDuplicateFiles([]);
    setDuplicateFilenames([]);
    toast.info(`Skipped ${skippedCount} duplicate file${skippedCount > 1 ? 's' : ''}`);
    
    // Upload pending non-duplicates if any
    const nonDupes = pendingNonDuplicatesRef.current;
    pendingNonDuplicatesRef.current = null;
    pendingDuplicatesRef.current = null;
    
    if (nonDupes && nonDupes.length > 0) {
      debug.log('[duplicates] uploading pending non-duplicates', { count: nonDupes.length });
      setShowProcessingPanel(true);
      const newProcessingFiles: ProcessingFile[] = nonDupes.map(file => ({
        id: crypto.randomUUID(),
        filename: file.name,
        status: 'uploading',
        progress: 0,
      }));
      setProcessingFiles(prev => [...prev, ...newProcessingFiles]);
      setUploadQueue(nonDupes);

      toast.info(`Uploading ${nonDupes.length} photo${nonDupes.length > 1 ? 's' : ''}...`);

      // Pass snapshot to avoid state race condition
      await processUploadQueue(nonDupes, newProcessingFiles);
      setUploadQueue([]);
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSkipAllDuplicates = async () => {
    debug.log('[duplicates] skip-all pressed');
    setShowDuplicateDialog(false);
    setSkipAllDuplicates(true);
    const skippedCount = duplicateFiles.length;
    setDuplicateFiles([]);
    setDuplicateFilenames([]);
    toast.success('Will skip duplicate files automatically in future');
    
    // Upload pending non-duplicates if any
    const nonDupes = pendingNonDuplicatesRef.current;
    pendingNonDuplicatesRef.current = null;
    pendingDuplicatesRef.current = null;
    
    if (nonDupes && nonDupes.length > 0) {
      debug.log('[duplicates] uploading pending non-duplicates (skip-all)', { count: nonDupes.length });
      setShowProcessingPanel(true);
      const newProcessingFiles: ProcessingFile[] = nonDupes.map(file => ({
        id: crypto.randomUUID(),
        filename: file.name,
        status: 'uploading',
        progress: 0,
      }));
      setProcessingFiles(prev => [...prev, ...newProcessingFiles]);
      setUploadQueue(nonDupes);

      toast.info(`Uploading ${nonDupes.length} photo${nonDupes.length > 1 ? 's' : ''}...`);

      // Pass snapshot to avoid state race condition
      await processUploadQueue(nonDupes, newProcessingFiles);
      setUploadQueue([]);
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const processUploadQueue = async (filesToProcess: File[], processingFilesSnapshot?: ProcessingFile[]) => {
    if (!user || !organizationId) return;

    console.group('Upload pipeline');
    debug.log('[processUploadQueue] start', { count: filesToProcess.length, organizationId, userId: user.id, processingFilesSnapshot: processingFilesSnapshot?.length });

    // Ensure uploads are not marked as paused when starting/continuing
    setIsUploadPaused(false);
    isUploadPausedRef.current = false;
    setShowProcessingPanel(true);

    // Keep a reference to this batch for potential resume
    lastBatchRef.current = filesToProcess;

    const { uploadPhoto } = await import("@/utils/photoUpload");
    uploadAbortController.current = new AbortController();

    for (let i = 0; i < filesToProcess.length; i++) {
      if (isUploadPausedRef.current || uploadAbortController.current.signal.aborted) {
        // Save remaining files to queue
        const remaining = filesToProcess.slice(i);
        console.warn('[processUploadQueue] paused/aborted — saving remaining files', { index: i, remaining: remaining.length, names: remaining.map(f => f.name) });
        setUploadQueue(remaining);
        console.groupEnd();
        return;
      }

      const file = filesToProcess[i];
      debug.log(`[upload:start] (${i + 1}/${filesToProcess.length})`, file.name);
      
      // Use snapshot if provided, otherwise use current state
      const filesToCheck = processingFilesSnapshot || processingFiles;
      const processingFile = filesToCheck.find(f => f.filename === file.name && f.status === 'uploading');
      
      if (!processingFile) {
        console.warn('[upload] no matching processing entry, skipping', file.name, { processingFilesCount: filesToCheck.length, snapshot: !!processingFilesSnapshot });
        continue;
      }

      const progressInterval = setInterval(() => {
        setProcessingFiles(prev =>
          prev.map(f =>
            f.id === processingFile.id && f.status === 'uploading'
              ? { ...f, progress: Math.min((f.progress || 0) + 10, 90) }
              : f
          )
        );
      }, 200);

      try {
        const result = await uploadPhoto({
          file,
          organizationId,
        });

        clearInterval(progressInterval);

        if (result.success && result.photoId) {
          debug.log('[upload:success]', { name: file.name, photoId: result.photoId });
          setProcessingFiles(prev =>
            prev.map(f =>
              f.id === processingFile.id
                ? { ...f, id: result.photoId!, status: 'processing', progress: 100, processingStartTime: Date.now() }
                : f
            )
          );
        } else {
          const errorMessage = result.error || 'Upload failed';
          console.error('[upload:failed]', { name: file.name, error: errorMessage });
          setProcessingFiles(prev =>
            prev.map(f =>
              f.id === processingFile.id 
                ? { ...f, status: 'failed', progress: 0, error: errorMessage } 
                : f
            )
          );
        }
      } catch (error) {
        clearInterval(progressInterval);
        const errorMessage = error instanceof Error ? error.message : 'Unknown upload error';
        console.error('[upload:error]', { name: file.name, error: errorMessage, raw: error });
        setProcessingFiles(prev =>
          prev.map(f =>
            f.id === processingFile.id 
              ? { ...f, status: 'failed', progress: 0, error: errorMessage } 
              : f
          )
        );
      }
    }

    console.groupEnd();
  };

  const handleRetryProcessing = async (photoId: string) => {
    try {
      // Update status to processing
      setProcessingFiles(prev =>
        prev.map(f => f.id === photoId ? { ...f, status: 'processing', progress: 100 } : f)
      );

      // Invoke the process-photo edge function
      const { error } = await supabase.functions.invoke('process-photo', {
        body: { photoId },
      });

      if (error) {
        console.error('Failed to retry processing:', error);
        const errorMessage = error.message || 'Retry failed';
        setProcessingFiles(prev =>
          prev.map(f => f.id === photoId ? { ...f, status: 'failed', error: errorMessage } : f)
        );
        toast.error('Failed to retry AI processing');
      } else {
        toast.success('Retrying AI processing...');
      }
    } catch (error) {
      console.error('Retry processing error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown retry error';
      setProcessingFiles(prev =>
        prev.map(f => f.id === photoId ? { ...f, status: 'failed', error: errorMessage } : f)
      );
      toast.error('Failed to retry AI processing');
    }
  };

  // Timeout detection for stuck processing photos (5 minutes)
  useEffect(() => {
    const TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
    
    const interval = setInterval(() => {
      setProcessingFiles(prev => {
        const now = Date.now();
        return prev.map(f => {
          if (f.status === 'processing' && f.processingStartTime) {
            const elapsed = now - f.processingStartTime;
            if (elapsed > TIMEOUT_MS) {
              console.warn(`Photo ${f.id} (${f.filename}) stuck in processing for ${Math.round(elapsed / 1000)}s, marking as failed`);
              toast.error(`Processing timeout for ${f.filename}`);
              return { ...f, status: 'failed' as const };
            }
          }
          return f;
        });
      });
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleGlobalDragLeave = (e: DragEvent) => {
      if (e.clientX === 0 && e.clientY === 0) {
        setIsDragging(false);
      }
    };

    const handleGlobalDrop = () => {
      setIsDragging(false);
    };

    document.addEventListener('dragleave', handleGlobalDragLeave);
    document.addEventListener('drop', handleGlobalDrop);

    return () => {
      document.removeEventListener('dragleave', handleGlobalDragLeave);
      document.removeEventListener('drop', handleGlobalDrop);
    };
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        onChange={handleFileInput}
        className="hidden"
      />
      
      {/* Desktop & Tablet Sidebar - Always visible on md+ */}
      <div className="hidden md:block">
        <AppSidebar
          folders={folders}
          selectedFolderId={selectedFolderId}
          onFolderSelect={(folderId) => {
            if (folderId) {
              // Replace folder filter with the selected one (only show 1 folder at a time)
              const newFolders = selectedFolderFilters.includes(folderId) && selectedFolderFilters.length === 1
                ? [] // If clicking the same single folder, clear it
                : [folderId]; // Otherwise, replace with the new folder
              updateFilters({ 
                folders: newFolders, 
                trash: false,
                tags: [],
                colors: [],
                rating: null,
                favorite: false,
                search: '',
                type: 'all'
              });
            }
            setSelectedFolderId(null); // Clear sidebar selection
          }}
          onToggleFolder={handleToggleFolder}
          onCreateFolder={() => setShowCreateFolderDialog(true)}
          totalPhotos={totalPhotosCount}
          trashCount={trashCount}
          piiCount={piiCount}
          noTagsCount={noTagsCount}
          noFoldersCount={noFoldersCount}
          hasCodeCount={hasCodeCount}
          hasPersonCount={hasPersonCount}
          illustratedCount={illustratedCount}
          errorCount={errorCount}
          onShowTrash={handleShowTrash}
          onShowAll={handleShowAll}
          onShowPII={handleShowPII}
          onShowNoTags={handleShowNoTags}
          onShowNoFolders={handleShowNoFolders}
          onShowHasCode={handleShowHasCode}
          onShowHasPerson={handleShowHasPerson}
          onShowIllustrated={handleShowIllustrated}
          onShowError={handleShowError}
          showTrash={showTrash}
          activeQuickLink={activeQuickLink}
          photoTypeFilter={photoTypeFilter}
          organizationId={organizationId}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          totalStorageBytes={totalStorageBytes}
        />
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="fixed left-0 top-0 h-full w-64 bg-card z-50 md:hidden">
            <AppSidebar
              folders={folders}
              selectedFolderId={selectedFolderId}
              onFolderSelect={(folderId) => {
                if (folderId) {
                  // Replace folder filter with the selected one (only show 1 folder at a time)
                  const newFolders = selectedFolderFilters.includes(folderId) && selectedFolderFilters.length === 1
                    ? [] // If clicking the same single folder, clear it
                    : [folderId]; // Otherwise, replace with the new folder
                  updateFilters({ 
                    folders: newFolders, 
                    trash: false,
                    tags: [],
                    colors: [],
                    rating: null,
                    favorite: false,
                    search: '',
                    type: 'all'
                  });
                }
                setSelectedFolderId(null); // Clear sidebar selection
                setMobileMenuOpen(false);
              }}
              onToggleFolder={handleToggleFolder}
              onCreateFolder={() => {
                setShowCreateFolderDialog(true);
                setMobileMenuOpen(false);
              }}
              onShowTrash={() => {
                handleShowTrash();
                setMobileMenuOpen(false);
              }}
              onShowAll={() => {
                handleShowAll();
                setMobileMenuOpen(false);
              }}
              onShowPII={() => {
                handleShowPII();
                setMobileMenuOpen(false);
              }}
              onShowNoTags={() => {
                handleShowNoTags();
                setMobileMenuOpen(false);
              }}
              onShowNoFolders={() => {
                handleShowNoFolders();
                setMobileMenuOpen(false);
              }}
              onShowHasCode={() => {
                handleShowHasCode();
                setMobileMenuOpen(false);
              }}
              onShowHasPerson={() => {
                handleShowHasPerson();
                setMobileMenuOpen(false);
              }}
              onShowIllustrated={() => {
                handleShowIllustrated();
                setMobileMenuOpen(false);
              }}
              onShowError={() => {
                handleShowError();
                setMobileMenuOpen(false);
              }}
          showTrash={showTrash}
          totalPhotos={totalPhotosCount}
          trashCount={trashCount}
          piiCount={piiCount}
          noTagsCount={noTagsCount}
          noFoldersCount={noFoldersCount}
          hasCodeCount={hasCodeCount}
          errorCount={errorCount}
          hasPersonCount={hasPersonCount}
          illustratedCount={illustratedCount}
          activeQuickLink={activeQuickLink}
          photoTypeFilter={photoTypeFilter}
          organizationId={organizationId}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          totalStorageBytes={totalStorageBytes}
        />
          </div>
        </>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {isMobile ? (
          <MobileToolbar
            searchQuery={localSearchQuery}
            onSearchChange={handleSearchChange}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(value) => updateFilters({ page: value })}
            selectedTags={selectedTags}
            onTagsChange={(value) => updateFilters({ tags: value })}
            selectedFolders={selectedFolderFilters}
            onFoldersChange={(value) => updateFilters({ folders: value })}
            selectedColors={selectedColors}
            onColorsChange={(value) => updateFilters({ colors: value })}
            includeSimilarColors={includeSimilarColors}
            onIncludeSimilarColorsChange={(value) => updateFilters({ includeSimilarColors: value })}
            ratingFilter={ratingFilter}
            onRatingFilterChange={(value) => updateFilters({ rating: value })}
            photoTypeFilter={photoTypeFilter}
            onPhotoTypeFilterChange={(value) => updateFilters({ type: value })}
            favoriteFilter={favoriteFilter}
            onFavoriteFilterChange={(value) => updateFilters({ favorite: value })}
            availableTags={availableTags}
            availableFolders={availableFolders}
            thumbnailSize={thumbnailSize}
            onThumbnailSizeChange={setThumbnailSize}
            organizationId={organizationId}
            onTagCreated={() => {
              if (organizationId) {
                supabase
                  .from('tags')
                  .select('id, name, color')
                  .eq('organization_id', organizationId)
                  .order('name')
                  .then(({ data }) => {
                    if (data) setAvailableTags(data);
                });
              }
            }}
            sidebarContent={
              <AppSidebar
                folders={folders}
                selectedFolderId={selectedFolderId}
                onFolderSelect={(folderId) => {
                  if (folderId) {
                    const newFolders = selectedFolderFilters.includes(folderId) && selectedFolderFilters.length === 1
                      ? []
                      : [folderId];
                    updateFilters({ 
                      folders: newFolders, 
                      trash: false,
                      tags: [],
                      colors: [],
                      rating: null,
                      favorite: false,
                      search: '',
                      type: 'all'
                    });
                  }
                  setSelectedFolderId(null);
                }}
                onToggleFolder={handleToggleFolder}
                onCreateFolder={() => setShowCreateFolderDialog(true)}
                onShowTrash={handleShowTrash}
                onShowAll={handleShowAll}
                onShowPII={handleShowPII}
                onShowNoTags={handleShowNoTags}
                onShowNoFolders={handleShowNoFolders}
                onShowHasCode={handleShowHasCode}
                onShowHasPerson={handleShowHasPerson}
                onShowIllustrated={handleShowIllustrated}
                onShowError={handleShowError}
                showTrash={showTrash}
                totalPhotos={totalPhotosCount}
                trashCount={trashCount}
                piiCount={piiCount}
                noTagsCount={noTagsCount}
                noFoldersCount={noFoldersCount}
                hasCodeCount={hasCodeCount}
                errorCount={errorCount}
                hasPersonCount={hasPersonCount}
                illustratedCount={illustratedCount}
                activeQuickLink={activeQuickLink}
                photoTypeFilter={photoTypeFilter}
                organizationId={organizationId}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                totalStorageBytes={totalStorageBytes}
              />
            }
          />
        ) : (
          <Toolbar
            title={getCurrentFolderName()}
            searchQuery={localSearchQuery}
            onSearchChange={handleSearchChange}
            currentPage={currentPage}
            totalPages={totalPages}
          totalCount={totalCount}
          onPageChange={(value) => updateFilters({ page: value })}
          selectedTags={selectedTags}
          onTagsChange={(value) => updateFilters({ tags: value })}
          selectedFolders={selectedFolderFilters}
          onFoldersChange={(value) => updateFilters({ folders: value })}
          selectedColors={selectedColors}
          onColorsChange={(value) => updateFilters({ colors: value })}
          includeSimilarColors={includeSimilarColors}
          onIncludeSimilarColorsChange={(value) => updateFilters({ includeSimilarColors: value })}
          ratingFilter={ratingFilter}
          onRatingFilterChange={(value) => updateFilters({ rating: value })}
          photoTypeFilter={photoTypeFilter}
          onPhotoTypeFilterChange={(value) => updateFilters({ type: value })}
          favoriteFilter={favoriteFilter}
          onFavoriteFilterChange={(value) => updateFilters({ favorite: value })}
          availableTags={availableTags}
          availableFolders={availableFolders}
          thumbnailSize={thumbnailSize}
          onThumbnailSizeChange={setThumbnailSize}
          onReprocessFailed={handleReprocessFailed}
          onReprocessAll={handleReprocessAll}
          onReprocessNoTags={() => setShowNoTagsDialog(true)}
          organizationId={organizationId}
          onTagCreated={() => {
            // Reload tags when a new one is created
            if (organizationId) {
              supabase
                .from('tags')
                .select('id, name, color')
                .eq('organization_id', organizationId)
                .order('name')
                .then(({ data }) => {
                  if (data) setAvailableTags(data);
              });
            }
          }}
          sortField={sortField}
          sortDirection={sortDirection}
          onSortChange={(field, direction) => {
            updateFilters({ sortField: field, sortDirection: direction });
          }}
          onClearAllFilters={() => {
            setSelectedFolderId(null);
            updateFilters({ 
              search: '',
              tags: [],
              folders: [],
              colors: [],
              includeSimilarColors: false,
              rating: null,
              type: 'all',
              favorite: false
            });
          }}
          userEmail={user?.email}
          onProfileClick={() => navigate('/profile')}
          onSettingsClick={() => navigate('/settings')}
          onSignOut={handleSignOut}
          />
        )}

        <div
          className="flex-1 flex min-h-0"
          onDragLeave={handleDragLeave}
        >
          <Gallery
            photos={photos}
            selectedPhotoId={selectedPhotoId}
            selectedPhotoIds={selectedPhotoIds}
            onPhotoSelect={setSelectedPhotoId}
            onPhotoToggle={handlePhotoToggle}
            onSelectAll={handleSelectAll}
            onSelectAllPhotos={handleSelectAllPhotos}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            isDragging={isDragging}
            onUploadClick={() => fileInputRef.current?.click()}
            thumbnailSize={thumbnailSize}
            onThumbnailSizeChange={setThumbnailSize}
            isLoading={isLoadingPhotos}
            showEmptyTrash={showTrash && trashCount > 0}
            onEmptyTrash={handleEmptyTrash}
            isEmptyingTrash={isEmptyingTrash}
            onBulkDelete={handleBulkDelete}
            onBulkAddTags={() => setShowBulkTagDialog(true)}
            onBulkRestore={handleBulkRestore}
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            itemsPerPage={itemsPerPage}
            onPageChange={(page) => updateFilters({ page })}
            sortField={sortField}
            sortDirection={sortDirection}
            onSortChange={(field, direction) => updateFilters({ sortField: field, sortDirection: direction })}
            hasActiveFilters={searchQuery.trim() !== '' || selectedTags.length > 0 || selectedFolderFilters.length > 0 || selectedColors.length > 0 || ratingFilter !== null || favoriteFilter || (photoTypeFilter && photoTypeFilter !== 'all')}
          />


          {user && (
            <BulkTagDialog
              open={showBulkTagDialog}
              onClose={() => setShowBulkTagDialog(false)}
              onSuccess={async () => {
                // Reload photos to get updated tag associations
              const result = await loadPhotos({
                page: currentPage,
                itemsPerPage,
                search: searchQuery,
                tagFilter: selectedTags,
                folderFilter: selectedFolderFilters,
                ratingFilter: ratingFilter,
                photoTypeFilter: photoTypeFilter,
                favoriteFilter: favoriteFilter,
                showTrash: showTrash,
                folderId: selectedFolderId,
                sortField,
                sortDirection,
              });
              setPhotos(result.photos);
              }}
              photoIds={selectedPhotoIds}
              organizationId={organizationId}
            />
          )}

          {selectedPhoto && !isMobile && (
            <DetailPanel
              photo={selectedPhoto}
              onClose={() => setSelectedPhotoId(null)}
              onPhotoUpdate={(updatedPhoto) => {
                setPhotos(prev => prev.map(p => 
                  p.id === updatedPhoto.id ? updatedPhoto : p
                ));
              }}
            />
          )}

          {/* Mobile Detail Drawer */}
          {selectedPhoto && isMobile && (
            <Drawer open={!!selectedPhoto} onOpenChange={(open) => !open && setSelectedPhotoId(null)}>
              <DrawerContent className="max-h-[90vh]">
                <div className="overflow-y-auto">
                  <DetailPanel
                    photo={selectedPhoto}
                    onClose={() => setSelectedPhotoId(null)}
                    onPhotoUpdate={(updatedPhoto) => {
                      setPhotos(prev => prev.map(p => 
                        p.id === updatedPhoto.id ? updatedPhoto : p
                      ));
                    }}
                  />
                </div>
              </DrawerContent>
            </Drawer>
          )}
        </div>

        {showProcessingPanel && (
          <ProcessingPanel
            files={processingFiles}
            onCancel={handleCancelProcessing}
            onCancelAll={handleCancelAllProcessing}
            onClose={handleCancelAllProcessing}
            onRetry={handleRetryProcessing}
            onPause={handlePauseUpload}
            onContinue={handleContinueUpload}
            isPaused={isUploadPaused}
          />
        )}


        <ReprocessProgressDialog
          open={isReprocessing}
          onOpenChange={setIsReprocessing}
          total={reprocessProgress.total}
          processed={reprocessProgress.processed}
          successful={reprocessProgress.successful}
          failed={reprocessProgress.failed}
          currentPhoto={reprocessProgress.currentPhoto}
          onStop={() => { shouldStopReprocessingRef.current = true; }}
        />

        <ReprocessNoTagsDialog
          open={showNoTagsDialog}
          onOpenChange={setShowNoTagsDialog}
          organizationId={organizationId}
          photoCount={noTagsCount}
        />

        <EmptyTrashDialog
          open={showEmptyTrashDialog}
          onOpenChange={setShowEmptyTrashDialog}
          onConfirm={handleConfirmEmptyTrash}
          trashCount={trashCount}
          isLoading={isEmptyingTrash}
        />

        <DeletePhotosDialog
          open={showDeletePhotosDialog}
          onOpenChange={setShowDeletePhotosDialog}
          onConfirm={handleConfirmBulkDelete}
          photoCount={selectedPhotoIds.length}
          isLoading={isDeletingPhotos}
        />

        <CreateFolderDialog
          open={showCreateFolderDialog}
          onOpenChange={setShowCreateFolderDialog}
          onCreateFolder={async (folderName) => {
            if (!organizationId) throw new Error('No organization selected');

            const { data, error } = await supabase
              .from('folders')
              .insert({
                name: folderName,
                organization_id: organizationId,
                color: `hsl(${Math.random() * 360}, 70%, 50%)`,
                icon: '📁',
              })
              .select()
              .single();

            if (error) throw error;

            if (data) {
              toast('Folder created', {
                description: `"${folderName}" has been created.`,
              });
              await loadFoldersFromDB();
            }
          }}
        />

        <DuplicateFilesDialog
          open={showDuplicateDialog}
          duplicateCount={duplicateFiles.length}
          duplicateFiles={duplicateFilenames}
          onOverwrite={handleOverwriteDuplicates}
          onSkip={handleSkipDuplicates}
          onSkipAll={handleSkipAllDuplicates}
        />

        {isMobile && (
          <MobileBottomBar
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            itemsPerPage={itemsPerPage}
            onPageChange={(value) => updateFilters({ page: value })}
          />
        )}
      </div>
    </div>
  );
};

export default Index;
